'use client';
/**
 * Présences (lot 3) — le réalisé confronté au planning.
 *
 * La colonne vertébrale de cet écran : **« pointé » et « retenu » s'affichent
 * côte à côte**. Un logiciel qui remplacerait l'un par l'autre rendrait la paie
 * invérifiable — et l'écart indémontrable en audit. Corriger, c'est écrire à
 * côté du pointage avec un motif, jamais par-dessus.
 *
 * Le rapport d'import n'est volontairement pas un message fugace : les lignes
 * rejetées et les badges inconnus sont des heures de travail qui manqueraient
 * sans que personne ne les réclame. Il reste jusqu'à ce qu'on le ferme.
 */
import React from 'react';
import { enTeteCollant, Tableau } from '@/shared/Tableau';
import { useGed, userName, joursSemaine, lundiDe } from '@/lib/store';
import { heuresLisibles, jourIso, isoJour, fmtDateHeure, fmtDate } from '@/lib/meta';
import type { RhJournee } from '@/lib/types';
import type { RhPointage, ConstatPointage, PropositionAbsence, SensSuspect } from '@/lib/types';
import { useRemonteEnHaut, I, card, inputStyle, EmptyState, ConfirmButton, useEscape, Help, FilterBar, Skeleton, TitrePage, FieldError } from '@/components/ui';
import { AccesRefuseRh, Modale, lbl } from '@/components/screens/RhAbsences';
import { useAutorisationResponsable, ChampsResponsable } from '@/components/AutorisationResponsable';
import { nomDe } from '@/lib/nom';
import { fetchEcriture } from '@/lib/ecrire';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
import { libelleMois, libelleJourCourt } from '@/lib/jour';
const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const ETATS: Record<string, { court: string; fond: string; texte: string; libelle: string }> = {
  conforme: { court: '', fond: 'transparent', texte: 'var(--text-body)', libelle: 'Conforme' },
  ecart: { court: '≠', fond: 'var(--amber-50)', texte: 'var(--amber-600)', libelle: 'Écart d’horaire' },
  anomalie: { court: '!', fond: 'var(--red-50)', texte: 'var(--red-600)', libelle: 'Pointage incomplet' },
  absence_justifiee: { court: 'A', fond: 'var(--gray-25)', texte: 'var(--text-muted)', libelle: 'Absence justifiée' },
  absence_non_pointee: { court: '?', fond: 'var(--red-50)', texte: 'var(--red-600)', libelle: 'Planifié, aucun pointage' },
  presence_non_planifiee: { court: '+', fond: 'var(--amber-50)', texte: 'var(--amber-600)', libelle: 'Présent sans être planifié' },
  /* ⚠️ GRIS, PAS ROUGE — et c'est tout le propos (RQ, 26/08/2026). « Aucun
     fichier importé » n'est pas un reproche adressé à quelqu'un : c'est une
     mesure qui manque au laboratoire. Le peindre en rouge à côté des vraies
     absences ferait exactement ce que cet état existe pour empêcher. */
  /* Élargi le 29/09/2026 : « non mesuré » couvre aussi la personne non soumise
     au pointage, sans badge, ou pas encore inscrite à la badgeuse. */
  non_mesure: { court: '·', fond: 'var(--gray-25)', texte: 'var(--text-muted)', libelle: 'Planifié — non mesuré (aucun fichier importé, pas de badge, ou non soumis au pointage)' },
};

/** Miroir de `LIBELLES_ANOMALIE` (`backend/src/rh-pointage.ts`) : les deux
 *  dépôts ne partagent pas de code — si l'une bouge, bouger l'autre. */
export const LIB_ANOMALIE: Record<string, string> = {
  sortie_manquante: 'sortie non pointée',
  entree_manquante: 'entrée non pointée',
  duree_invraisemblable: 'durée invraisemblable (sortie oubliée ?)',
  double_badgeage: 'badge repassé',
  appui_doute: 'passage refusé par ZKTime (réserve)',
  absence_contredite: 'absence validée, mais le badge a été lu ce jour-là',
  badge_manquant: 'un badge manque au milieu de la vacation',
  vacation_non_badgee: 'vacation planifiée sans aucun badge (ex. garde non badgée)',
  enchainement_presume: 'poste et garde enchaînés sans badge à la relève — prévalidé (relève à l’heure du planning)',
  sens_contredit: 'Entrée/Sortie mal choisi sur la badgeuse (heures du premier au dernier passage)',
  horaire_decale: 'venu(e) à un autre horaire que celui du planning — planning à corriger',
  retard_tolere: 'arrivée après l’heure planifiée, tolérée par décision de la RQ',
};

/**
 * LA DÉFINITION DE CHAQUE ANOMALIE ET RÉSERVE, en clair (RQ, 29/09/2026 :
 * « explicite-moi sa définition quand je clique dessus »). Le libellé court
 * dit QUOI ; la définition dit POURQUOI, ce que l'outil en a fait, et ce qu'on
 * peut y faire. Miroir des décisions de `backend/src/rh-pointage.ts`.
 */
const DEF_ANOMALIE: Record<string, { bloque: boolean; texte: string }> = {
  appui_doute: { bloque: false, texte: 'La badgeuse ZKTime a noté ce passage « Invalide » : son horaire programmé ne prévoit pas cette heure-là (surtout l’après-midi et la garde). Le passage a eu lieu, il est importé et compté ; le « ≈ » rappelle seulement que ZKTime l’a refusé. Simple réserve depuis le 29/09/2026 : il ne bloque pas la validation. Pour le lever : « Confirmer » dans le tableau des passages ci-dessous.' },
  sens_contredit: { bloque: false, texte: 'Un seul lecteur : c’est la personne qui choisit Entrée ou Sortie, et elle se trompe environ une fois sur douze. Les heures sont prises du premier au dernier passage, pause éventuelle comprise. Simple réserve.' },
  horaire_decale: { bloque: false, texte: 'La personne est venue, mais pas à l’heure planifiée (échange de poste non reporté au planning). Les heures sont celles du badge ; aucun retard n’est calculé. C’est le planning qu’il faut corriger. Simple réserve.' },
  retard_tolere: { bloque: false, texte: 'Arrivée après l’heure planifiée, tolérée par décision de la RQ (déclarée sur la fiche de la personne). Le retard n’est pas compté et ne forme pas de série opposable. Simple réserve.' },
  enchainement_presume: { bloque: false, texte: 'Deux vacations enchaînées (poste puis garde, par exemple) sans badge à la relève : l’outil présume que la personne est restée et prend l’heure de relève du planning. Prévalidé depuis le 01/11/2026 (« ça arrive tout le temps ») : la journée n’est plus à traiter, le retenu est le prévu au plus. Si la personne est en fait partie, corrigez la journée.' },
  sortie_manquante: { bloque: true, texte: 'Une entrée sans sortie : la durée ne peut pas être mesurée (0 h comptée). Retenez les heures réelles avec un motif.' },
  entree_manquante: { bloque: true, texte: 'Une sortie sans entrée : la durée ne peut pas être mesurée (0 h comptée). Retenez les heures réelles avec un motif.' },
  vacation_non_badgee: { bloque: true, texte: 'Une vacation planifiée (souvent la garde de nuit) n’a AUCUN passage, alors que la personne a badgé une autre vacation le même jour. Garde oubliée, remplacée sans le dire, ou non tenue ? 0 h comptée : confirmez avec la personne, puis retenez les heures réelles avec un motif ou corrigez le planning.' },
  badge_manquant: { bloque: true, texte: 'Nombre impair de passages dans la vacation : un badge manque au milieu, et rien ne dit lequel. Les heures sont prises du premier au dernier passage ; à vérifier.' },
  absence_contredite: { bloque: true, texte: 'Une absence validée couvre ce jour, et le badge a pourtant été lu. Congé mal saisi, badge prêté, retour anticipé ? Corrigez l’absence (depuis le contrôle des données) ou retenez 0 h.' },
  duree_invraisemblable: { bloque: true, texte: 'Plus de 16 h entre l’entrée et la sortie : une sortie a sans doute été oubliée. Rien n’est compté ; retenez les heures réelles avec un motif.' },
  double_badgeage: { bloque: true, texte: 'Le badge a été repassé deux fois dans le même sens en moins de 2 min : le second passage est ignoré.' },
};

/** Une pastille d'anomalie : un clic déplie sa définition. */
function PastilleAnomalie({ code }: { code: string }) {
  const [ouvert, setOuvert] = React.useState(false);
  const def = DEF_ANOMALIE[code];
  const bloque = def ? def.bloque : true;
  return (
    <span style={{ display: 'inline-block', marginInlineEnd: 6, marginBottom: 4 }}>
      <button type="button" aria-expanded={ouvert} onClick={() => setOuvert(!ouvert)}
        style={{
          border: `1px solid ${bloque ? 'var(--red-500)' : 'var(--border-default)'}`,
          background: bloque ? 'var(--red-50)' : 'var(--gray-25)', color: bloque ? 'var(--red-600)' : 'var(--text-body)',
          borderRadius: 'var(--radius-md)', padding: '2px 9px', fontSize: 12, cursor: 'pointer',
        }}>
        {LIB_ANOMALIE[code] || code} <span aria-hidden>{ouvert ? '▴' : '▾'}</span>
      </button>
      {ouvert && def && (
        <span style={{ display: 'block', fontSize: 12, lineHeight: 1.45, margin: '4px 0 2px', padding: '6px 8px',
          background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', maxWidth: 520 }}>
          <strong>{bloque ? 'Bloque la validation. ' : 'Simple réserve — ne bloque pas. '}</strong>{def.texte}
        </span>
      )}
    </span>
  );
}

/** « 07:05 » depuis un horodatage ISO ; « +1 » quand le passage tombe le
 *  lendemain du jour de la journée (sortie de garde). */
function heureDe(ts: string | null, jour: string): string | null {
  if (!ts) return null;
  const hm = ts.slice(11, 16);
  return jourIso(ts) > jour ? `${hm}+1` : hm;
}

/**
 * LES ENTRÉES ET SORTIES DANS LA CASE (RQ, 29/09/2026) : « je ne vois pas les
 * données d'entrée sortie dans chaque cellule ». La case ne montrait qu'un
 * total — impossible d'y lire qu'une sortie manque, ni laquelle. Chaque
 * présence devient une ligne `07:05 → 16:27` ; un côté manquant s'écrit « ? »
 * en rouge, un passage refusé par la badgeuse porte un « ≈ ».
 */
function Passages({ j }: { j: RhJournee }) {
  const segs = (j.segments || []).filter((sg) => sg.debut || sg.fin);
  if (!segs.length) return null;
  const manque = <span style={{ color: 'var(--red-600)', fontWeight: 700 }}>?</span>;
  return (
    <div style={{ fontSize: 11.5, lineHeight: 1.35, fontVariantNumeric: 'tabular-nums', color: 'var(--text-body)', marginTop: 2 }}>
      {segs.slice(0, 3).map((sg, i) => {
        const toutes = sg.anomalies?.length ? sg.anomalies : (sg.anomalie ? [sg.anomalie] : []);
        const doute = toutes.includes('appui_doute');
        const garde = /GARDE/.test(sg.poste || '');
        return (
          <div key={i} style={{ whiteSpace: 'nowrap' }}
            title={[sg.poste ? `Poste ${sg.poste}${sg.attenduDebut ? ` (prévu ${sg.attenduDebut.slice(11, 16)} → ${String(sg.attenduFin || '').slice(11, 16)})` : ''}` : '',
              ...toutes.map((a) => LIB_ANOMALIE[a] || a)].filter(Boolean).join(' — ') || undefined}>
            {garde && <span style={{ color: 'var(--text-muted)' }}>garde </span>}
            {heureDe(sg.debut, j.date) ?? manque} → {heureDe(sg.fin, j.date) ?? manque}
            {doute && <span style={{ color: 'var(--amber-600)', fontWeight: 700 }} aria-label="passage refusé par la badgeuse"> ≈</span>}
            {toutes.includes('sens_contredit') && <span style={{ color: 'var(--text-muted)' }} aria-label="entrée/sortie mal choisi sur la badgeuse"> ⇄</span>}
            {toutes.includes('enchainement_presume') && <span style={{ color: 'var(--amber-600)', fontWeight: 700 }} aria-label="relève présumée"> ⌁</span>}
            {toutes.includes('horaire_decale') && <span style={{ color: 'var(--amber-600)' }} aria-label="horaire différent du planning"> ↔</span>}
          </div>
        );
      })}
      {segs.length > 3 && <div style={{ color: 'var(--text-muted)' }}>+{segs.length - 3}</div>}
    </div>
  );
}

const GRAVITE: Record<ConstatPointage['gravite'], { libelle: string; fond: string; texte: string }> = {
  bloquant: { libelle: 'À trancher', fond: 'var(--red-50)', texte: 'var(--red-600)' },
  attention: { libelle: 'À vérifier', fond: 'var(--amber-50)', texte: 'var(--amber-600)' },
  info: { libelle: 'Pour information', fond: 'var(--gray-25)', texte: 'var(--text-muted)' },
};

/**
 * CORRIGER L'ABSENCE D'APRÈS LE BADGE (RQ, 29/09/2026 : « l'outil me propose
 * selon l'inadéquation observée »). Trois voies, jamais appliquées d'office :
 * ajuster les dates, annuler, ou conserver le congé et ramener les journées
 * badgées à 0 h. Le motif est pré-rédigé à partir de ce que le badge montre —
 * on le relit, on peut le réécrire, il part au journal.
 */
function CorrectionAbsence({ c }: { c: ConstatPointage }) {
  const appliquer = useGed((s) => s.appliquerPropositionAbsence);
  const props = c.propositions || [];
  const [choix, setChoix] = React.useState(0);
  const [motif, setMotif] = React.useState(props[0]?.motifSuggere || '');
  const [envoi, setEnvoi] = React.useState(false);
  const [ouvert, setOuvert] = React.useState(false);
  if (!c.absenceId || !props.length) return null;
  const p: PropositionAbsence = props[choix]!;
  const court = motif.trim().length < 20;
  const long = motif.trim().length > 255;
  if (!ouvert) {
    return (
      <Bouton variante="primaire" taille="sm" type="button" onClick={() => setOuvert(true)} style={{ marginTop: 8 }}>
        Corriger l’absence…
      </Bouton>
    );
  }
  return (
    <div style={{ marginTop: 8, padding: 10, background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', display: 'grid', gap: 8 }}>
      <strong style={{ fontSize: 12.5 }}>Ce que le badge suggère :</strong>
      {props.map((x, i) => (
        <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
          <input type="radio" name={`corr-${c.absenceId}`} checked={choix === i}
            onChange={() => { setChoix(i); setMotif(x.motifSuggere); }} />
          <span>{x.libelle}</span>
        </label>
      ))}
      <label style={{ ...lbl, fontWeight: 600 }}>
        Motif <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(20 à 255 caractères — il part au journal)</span>
        <textarea rows={3} style={{ ...inputStyle, width: '100%' }} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </label>
      {(court || long) && (
        <FieldError>{court ? `Motif trop court : ${motif.trim().length} caractère(s) sur 20.` : `Motif trop long : ${motif.trim().length} caractères sur 255.`}</FieldError>
      )}
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
        {p.type === 'ajuster' && 'Le solde de congés est recalculé sur les nouvelles dates, et les journées badgées redeviennent du travail.'}
        {p.type === 'annuler' && 'Les jours décomptés sont recrédités au solde. Les journées badgées redeviennent du travail.'}
        {p.type === 'conserver' && 'Le congé ne change pas. Les journées badgées pendant l’absence sont retenues à 0 h, avec ce motif ; le pointage d’origine reste visible.'}
      </p>
      <PiedActions taille="sm" annuler={{ onClick: () => setOuvert(false), disabled: envoi }} principale={{ libelle: 'Appliquer', libelleChargement: 'Application…', chargement: envoi, disabled: court || long || envoi, onClick: async () => { setEnvoi(true); const ok = await appliquer(c, p, motif.trim()); setEnvoi(false); if (ok) setOuvert(false); } }} />
    </div>
  );
}

/** Les constats, du plus grave au plus anodin. Un clic sur une date ouvre sa
 *  semaine : le constat dit OÙ regarder, la grille montre QUOI. */
function ListeConstats({ constats, onJour }: { constats: ConstatPointage[]; onJour?: (d: string) => void }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
      {constats.map((c, i) => {
        const g = GRAVITE[c.gravite];
        return (
          <li key={`${c.code}-${c.orgId || ''}-${i}`} style={{ borderInlineStart: `3px solid ${g.texte}`, background: 'var(--surface-card)', padding: '8px 10px', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ background: g.fond, color: g.texte, fontWeight: 700, fontSize: 11, borderRadius: 'var(--radius-xs)', padding: '1px 6px', whiteSpace: 'nowrap' }}>{g.libelle}</span>
              <strong style={{ fontSize: 13 }}>{c.titre}</strong>
            </div>
            <p style={{ fontSize: 12.5, margin: '4px 0 0' }}>{c.detail}</p>
            <p style={{ fontSize: 12.5, margin: '2px 0 0', color: 'var(--text-muted)' }}>→ {c.action}</p>
            {c.absenceId && c.propositions?.length ? <CorrectionAbsence c={c} /> : null}
            {onJour && c.dates.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                {Array.from(new Set(c.dates.map((d) => lundiDe(new Date(`${d}T12:00:00`))))).slice(0, 6).map((lundi) => (
                  <Bouton taille="sm" key={lundi} type="button" onClick={() => onJour(lundi)}>
                    semaine du {fmtJour(lundi)}
                  </Bouton>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const SENS_LIB = (s?: string | null) => (s === 'entree' ? 'Entrée' : s === 'sortie' ? 'Sortie' : '—');

/**
 * LES SENS À VÉRIFIER (RQ, 30/09/2026 : « que l'outil me montre les données
 * et me propose les possibles erreurs de sens en me laissant la main »).
 *
 * Une ligne par passage suspect : ce que la personne a choisi sur la machine,
 * ce que l'outil propose, et POURQUOI. Rien n'est corrigé d'office. On corrige
 * ou on laisse tel quel, un par un ou en lot, toujours avec un motif ; le
 * passage d'origine reste dans le brut.
 */
function PanneauSens({ mois, onJour }: { mois: { du: string; au: string; libelle: string }; onJour: (d: string) => void }) {
  const rhSensSuspects = useGed((s) => s.rhSensSuspects);
  const loadSensSuspects = useGed((s) => s.loadSensSuspects);
  const verifier = useGed((s) => s.verifierPointage);
  const loadPresences = useGed((s) => s.loadPresences);
  React.useEffect(() => { void loadSensSuspects(mois.du, mois.au); }, [mois.du, mois.au, loadSensSuspects]);
  if (!rhSensSuspects || rhSensSuspects.du !== mois.du) return null;
  const l = rhSensSuspects.suggestions;
  if (!l.length) return null;

  /* L10 (11/10/2026) : le lot est celui du SOCLE (niveau avancé) — on coche, on
     choisit « Corriger » ou « Laisser tel quel », on relit le motif commun
     (10 caractères au moins, il part au journal de CHAQUE passage), on confirme.
     Rien n'est changé d'office ; le passage d'origine reste visible au pointage brut. */
  const MOTIF_DEFAUT = 'Entrée/Sortie mal choisi sur la badgeuse : sens corrigé d’après la place du passage dans la vacation.';
  const appliquer = async (x: SensSuspect, motif: string, garder: boolean) => {
    const ok = await verifier(x.documentId, 'sens', motif + (garder ? ' (sens déclaré conservé après vérification)' : ''),
      garder ? (x.declare || x.propose) : x.propose, true);
    if (!ok) throw new Error('vérification refusée par le moteur');
  };
  const recharger = async () => { await Promise.all([loadPresences(), loadSensSuspects(mois.du, mois.au)]); };

  return (
    <details style={{ ...card, padding: '12px 14px', marginBottom: 12, background: 'var(--gray-25)' }}>
      <summary style={{ cursor: 'pointer', fontSize: 13.5, fontWeight: 600 }}>
        Sens à vérifier — {mois.libelle} : {l.length} passage(s) dont l’Entrée/Sortie semble inversée
      </summary>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 8px' }}>
        Proposition d’après la place du passage dans sa vacation : le premier devrait être une entrée, le dernier une
        sortie. Cochez les passages, puis « Corriger » ou « Laisser tel quel » : le motif est demandé une fois et
        journalisé sur chaque passage. Rien n’est changé d’office — et le passage d’origine reste visible au pointage brut.
      </p>
      <Tableau
        id="rh-sens-suspects"
        niveau="avance"
        densite="compacte"
        lignes={l}
        cleLigne={(x) => x.documentId}
        vide={{ titre: 'Aucun passage au sens douteux.' }}
        colonnes={[
          { cle: 'nom', libelle: 'Qui', valeur: (x) => x.nom, epinglee: true, obligatoire: true, style: { fontWeight: 600 }, rendu: (x) => x.nom },
          {
            cle: 'ts', libelle: 'Jour · heure', type: 'date', valeur: (x) => x.ts, style: { whiteSpace: 'nowrap' },
            rendu: (x) => (
              <>
                <button type="button" style={{ background: 'none', border: 0, padding: 0, color: 'var(--color-primary-active)', cursor: 'pointer', font: 'inherit' }}
                  title={`Ouvrir la semaine du ${fmtDate(jourIso(x.ts))}`}
                  onClick={(e) => { e.stopPropagation(); onJour(lundiDe(new Date(`${jourIso(x.ts)}T12:00:00`))); }}>
                  {fmtJour(jourIso(x.ts))}
                </button>{' '}{x.ts.slice(11, 16)}
              </>
            ),
          },
          {
            cle: 'declare', libelle: 'Choisi', type: 'liste', valeur: (x) => x.declare || '', libelleValeur: (v) => SENS_LIB(v || null),
            rendu: (x) => <>{SENS_LIB(x.declare)}{x.note && x.note !== 'OK' ? <span style={{ color: 'var(--amber-600)' }}> ≈</span> : null}</>,
          },
          { cle: 'propose', libelle: 'Proposé', type: 'liste', valeur: (x) => x.propose, libelleValeur: (v) => SENS_LIB(v), style: { fontWeight: 700 }, rendu: (x) => SENS_LIB(x.propose) },
          { cle: 'raison', libelle: 'Pourquoi', valeur: (x) => `${x.raison} (${x.poste})`, style: { color: 'var(--text-muted)' }, rendu: (x) => `${x.raison} (${x.poste})` },
        ]}
        actionsLot={[
          {
            cle: 'corriger', libelle: 'Corriger', nature: 'autre', quoi: 'passages',
            motif: { libelle: 'Motif de la correction', defaut: MOTIF_DEFAUT, min: 10 },
            executer: (x, motif) => appliquer(x, motif, false), apresLot: recharger,
          },
          {
            cle: 'laisser', libelle: 'Laisser tel quel', nature: 'autre', quoi: 'passages',
            motif: { libelle: 'Motif de la vérification', defaut: MOTIF_DEFAUT, min: 10 },
            executer: (x, motif) => appliquer(x, motif, true), apresLot: recharger,
          },
        ]}
      />
    </details>
  );
}

/** Les colonnes du récapitulatif : clé, en-tête, aide au survol. */
const COLS_RECAP: [string, string, string][] = [
  ['nom', 'Salarié', ''],
  /* CE QUI RESTE À FAIRE D'ABORD (RQ, 01/10/2026) — et les colonnes qui ne
     servaient pas à la revue retirées (non mesurés, temps en moins/en plus,
     écart net, sens corrigés/incohérents, refusés ZKTime, gardes, dimanches) :
     elles restent lisibles dans la fiche de chacun. */
  ['joursNonValides', 'Non validés', 'Journées du mois pas encore validées pour la paie'],
  ['anomaliesOuvertes', 'À corriger', 'Journées au pointage incomplet, ni corrigées ni validées : elles bloquent la validation'],
  ['joursPlanifies', 'Jours planif.', 'Jours où la personne est au planning (hors garde)'],
  ['joursTravailles', 'Jours travaillés', 'Jours avec des heures retenues, ou badgés'],
  ['joursAbsence', 'Absences', 'Jours couverts par une absence validée'],
  ['heuresPrevues', 'H. prévues', 'Heures planifiées sur les jours mesurés, hors absence'],
  ['heuresRealisees', 'H. réalisées', 'Temps réellement travaillé, extras compris'],
  ['heuresRetenues', 'H. retenues', 'Ce qui part en paie : le prévu au plus, plus les heures sup validées (ou les heures corrigées)'],
  ['surplusNonValide', 'Surplus (non heures sup)', 'Temps au-delà du prévu (plus de 15 min/jour) qui n’a pas été validé en heures supplémentaires — non payé'],
  ['heuresSupValidees', 'H. sup validées', 'Temps en plus validé en heures supplémentaires, avec un motif — payé'],
  ['retards', 'Retards', 'Nombre d’arrivées après la tolérance (horaires aménagés exclus)'],
  ['minutesRetard', 'Min. retard', 'Total des minutes de retard'],
  ['oublisBadge', 'Oublis badge', 'Journées avec une entrée ou une sortie non badgée'],
  ['sensASuivre', 'Sens à vérifier', 'Sens suspects encore non tranchés'],
];

export type Detail = {
  /** Journées à traiter hors rubriques (calculé à l'écran, RH seulement). */
  joursATraiter?: { date: string; raison: string }[];
  nom: string; mois: string;
  bilan: { heuresAttendues: number; heuresRealisees: number; ecart: number; joursMesures: number;
    heuresRetenues?: number; surplusNonValide?: number; heuresSupValidees?: number };
  exemplaires: { date: string; arrivee: string | null; depart: string | null; heures: number }[];
  arriveesALHeure: number; joursTravailles: number;
  plus: { date: string; prevu: number; realise: number; enPlus: number; valide?: number }[];
  jours: { attendus: number; valides: number; nonValides: number };
  departsAnticipes: { date: string; depart: string; finPrevue: string; minutes: number; postes: string[] }[];
  retards: { date: string; arrivee: string | null; minutes: number; postes: string[] }[];
  retardsToleres: { date: string; arrivee: string | null }[];
  oublis: { date: string; manque: string; postes: string[]; corrigee: boolean }[];
  sens: { date: string; heure: string; statut: 'corrige' | 'a_verifier'; de: string; vers: string }[];
};

const JOUR_LONG = (iso: string) => libelleJourCourt(iso);

/**
 * LE RELEVÉ DU MOIS D'UNE PERSONNE — bilan, côté positif, points d'attention
 * (30/09/2026). Partagé par la fiche d'entretien (RH) et « Mes présences » (la
 * personne) : les deux lisent EXACTEMENT les mêmes faits. `onCorriger` n'est
 * donné qu'à la RH.
 */
/** Les types de points d'attention et leur pastille (30/09/2026). Couleurs
 *  franches mais lisibles à l'impression en noir et blanc : le libellé est
 *  toujours écrit, la couleur ne fait que doubler. */
/** Les explications du bilan, au survol (RQ, 30/09/2026). */
const AIDE_BILAN: Record<string, string> = {
  'Heures attendues': 'Somme des horaires des postes du PLANNING (horaire aménagé les jours de post garde), sur les seuls jours comptés. Deux postes sur la même plage ne comptent qu’une fois. Une journée avec un oubli de badgeage non régularisé n’entre ni dans l’attendu ni dans le réalisé : elle y reviendra dès qu’elle sera corrigée.',
  'Heures réalisées': 'Temps de présence mesuré par la badgeuse, extras compris (arrivée en avance, départ tardif) ; ou les heures reprises par la RH quand la journée a été corrigée. Une journée avec un oubli de badgeage non régularisé n’entre ni dans l’attendu ni dans le réalisé : elle y reviendra dès qu’elle sera corrigée.',
  'Écart': 'Heures réalisées moins heures attendues. En rouge : moins que prévu ; en vert : autant ou plus.',
  'Heures retenues': 'Heures transmises à la paie, sur les journées validées : le prévu au plus, plus les heures sup validées. Une correction RH l’emporte.',
  'Surplus (non heures sup)': 'Temps au-delà du prévu (plus de 15 min par jour) qui n’a pas été validé en heures supplémentaires. Il est gardé pour information, il n’est pas payé.',
  'Heures sup validées': 'Temps en plus reconnu comme heures supplémentaires, avec un motif (bouton « Valider en heures sup » du jour par jour). Il part en paie.',
  'Jours comptés': 'Jours mesurés par la badgeuse, hors absences. Une journée avec un oubli de badgeage non régularisé n’entre ni dans l’attendu ni dans le réalisé : elle y reviendra dès qu’elle sera corrigée.',
  'Arrivées à l’heure': 'Jours où la première arrivée est à l’heure du premier poste prévu (tolérance comprise), sur les jours où la personne a badgé. Un horaire décalé n’est pas compté à l’heure.',
  'Total des retards': 'Somme des minutes de retard du mois (au-delà de la tolérance), mesurées sur le premier poste prévu de la journée, et le nombre de retards.',
  'Jours attendus (planning)': 'Nombre de jours où la personne figure au planning du mois (astreintes exclues).',
  'Jours validés': 'Journées de présence déjà validées pour la paie.',
  'Jours non validés': 'Journées de présence qui restent à valider (ou à corriger d’abord).',
};

/* LE DÉTAIL PAR VACATION (RQ, 30/09/2026 : « pourquoi je vois une seule
   vacation de 20 h ? »). La journée porte UN total — c'est ce qui part en paie —
   mais elle peut regrouper plusieurs vacations : le poste du matin ET la garde
   de 19:00, rattachée au jour où elle COMMENCE. On les montre une par une ;
   une heure du lendemain porte « (+1) ». */
type SegmentJour = NonNullable<RhJournee['segments']>[number];
export const heureJ = (iso: string | null | undefined, date: string) =>
  !iso ? '?' : `${iso.slice(11, 16)}${jourIso(iso) > date ? ' (+1)' : ''}`;
const dureeH = (a?: string | null, b?: string | null) =>
  a && b && b > a ? Math.round(((Date.parse(b) - Date.parse(a)) / 3600000) * 100) / 100 : null;
export function vacationsDe(j: RhJournee) {
  return (j.segments || []).map((sg: SegmentJour) => ({
    poste: sg.poste || '',
    badge: sg.debut || sg.fin ? `${heureJ(sg.debut, j.date)}→${heureJ(sg.fin, j.date)}` : null,
    heures: sg.debut && sg.fin ? Number(sg.heures || 0) : null,
    prevu: sg.attenduDebut ? `${heureJ(sg.attenduDebut, j.date)}→${heureJ(sg.attenduFin, j.date)}` : null,
    heuresPrevues: dureeH(sg.attenduDebut, sg.attenduFin),
    manque: !sg.debut && !sg.fin ? 'aucun badge' : !sg.debut ? 'entrée non badgée' : !sg.fin ? 'sortie non badgée' : null,
  }));
}

const TYPES_POINT = {
  retard: { libelle: 'Retard', fond: 'var(--amber-50)', texte: 'var(--amber-700)' },
  depart: { libelle: 'Départ anticipé', fond: 'var(--violet-50)', texte: 'var(--violet-600)' },
  oubli: { libelle: 'Oubli de badgeage', fond: 'var(--red-50)', texte: 'var(--red-600)' },
  sens: { libelle: 'Erreur de sens', fond: 'var(--blue-50)', texte: 'var(--blue-700)' },
  autre: { libelle: 'Autre', fond: 'var(--gray-50)', texte: 'var(--gray-700)' },
  plus: { libelle: 'Temps en plus', fond: 'var(--cyan-50)', texte: 'var(--cyan-800)' },
  alheure: { libelle: 'À l’heure', fond: 'var(--green-50)', texte: 'var(--green-700)' },
  absence: { libelle: 'Absence', fond: 'var(--gray-100)', texte: 'var(--gray-700)' },
  rien: { libelle: 'Sans remarque', fond: 'var(--gray-25)', texte: 'var(--text-muted)' },
} as const;

function PastilleType({ type, actif, onClick }: { type: keyof typeof TYPES_POINT; actif?: boolean; onClick?: () => void }) {
  const t = TYPES_POINT[type];
  const style = { display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 8px',
    background: t.fond, color: t.texte, border: `1px solid ${actif ? t.texte : 'var(--border-subtle)'}`, whiteSpace: 'nowrap',
    boxShadow: actif ? `0 0 0 1px ${t.texte}` : undefined,
    WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } as React.CSSProperties;
  /* Cliquable (30/09/2026) : un clic filtre le tableau sur ce type. */
  return onClick
    ? <button type="button" onClick={onClick} aria-pressed={!!actif} title={`Filtrer : ${t.libelle}`} style={{ ...style, cursor: 'pointer' }}>{t.libelle}</button>
    : <span style={style}>{t.libelle}</span>;
}

/** Ce que la fiche sait faire d'une journée quand la RH l'ouvre (30/09/2026). */
export type ActionsFiche = {
  journee: (date: string) => RhJournee | undefined;
  /** Toutes les journées du mois (pour « jour par jour »). */
  journees: RhJournee[];
  bloquee: (j: RhJournee) => boolean;
  onCorriger: (date: string) => void;
  onValider: (date: string) => void;
  onValiderMois: () => void;
  /** Valider les seules journées cochées (RQ, 01/11/2026 : « valider une
   *  sélection de lignes en laissant certaines »). */
  onValiderSelection?: (dates: string[]) => Promise<void>;
  /** Valider le temps en plus en heures supplémentaires (30/09/2026). */
  onHeuresSup?: (date: string, heures: number, motif: string) => void;
  /** Déclarer l'absence d'un jour « planifié, aucun pointage » (01/10/2026). */
  onDeclarerAbsence?: (date: string, motif: string) => Promise<void>;
  /** Les motifs d'absence du paramétrage RH. */
  motifsAbsence?: { code: string; label: string }[];
};

export function FicheReleve({ d, libMois, actions }: { d: Detail; libMois: string; actions?: ActionsFiche }) {
  const rhPostes = useGed((s) => s.rhPostes);
  /* Le filtre par pastille (30/09/2026) : vide = tout afficher. */
  const [filtre, setFiltre] = React.useState<string[]>([]);
  const [supOuvert, setSupOuvert] = React.useState<string | null>(null);
  /* DÉCLARER L'ABSENCE depuis la fiche (RQ, 01/10/2026 — Latifa le 12/09). */
  const [absOuvert, setAbsOuvert] = React.useState<string | null>(null);
  const [absMotif, setAbsMotif] = React.useState('');
  const [absEnvoi, setAbsEnvoi] = React.useState(false);
  const [supHeures, setSupHeures] = React.useState('');
  /* LA SÉLECTION À VALIDER (RQ, 01/11/2026), par DATE : une journée peut porter
     deux lignes (retard + oubli), les deux cases cochent la même journée. Ne
     se coche que ce qui se valide tel quel — ni validé, ni bloqué. */
  const [choisies, setChoisies] = React.useState<Set<string>>(new Set());
  const [envoiSelection, setEnvoiSelection] = React.useState(false);
  /* LES JOURNÉES VALIDÉES SE MASQUENT (RQ, 01/11/2026 : « une fois validée, je
     voudrais que la ligne soit masquée, avec un bouton pour démasquer — je ne
     vois que les présences non encore validées »). Seulement quand la fiche
     porte ses actions (RH) : la fiche remise à la personne montre tout. */
  const [voirValidees, setVoirValidees] = React.useState(false);
  const validables = React.useMemo(() => new Set((actions?.journees || [])
    .filter((j) => !j.validee && !actions!.bloquee(j)).map((j) => j.date)), [actions]);
  const cochees = [...choisies].filter((x) => validables.has(x)).sort();
  const basculerChoix = (date: string) => setChoisies((c) => {
    const n = new Set(c);
    if (n.has(date)) n.delete(date); else n.add(date);
    return n;
  });
  const [supMotif, setSupMotif] = React.useState('');
  const basculerFiltre = (t: string) => setFiltre((f) => (f.includes(t) ? f.filter((x) => x !== t) : [...f, t]));
  /* LE POSTE DU POINT D'ATTENTION (RQ, 30/09/2026). Libellé du référentiel,
     sinon le code ; la garde en toutes lettres. */
  const libellePoste = (code: string) => (code === 'GARDE' ? 'Garde'
    : rhPostes.find((x) => x.code === code)?.libelle || code);
  const td: React.CSSProperties = { padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderBottom: '1px solid var(--border-subtle)', fontSize: 'var(--tableau-ligne-compacte-police)', textAlign: 'start', verticalAlign: 'top' };
  /* LA JOURNÉE, À CÔTÉ DE L'ANOMALIE (RQ, 30/09/2026 : « les corrections
     simplement à côté de l'anomalie, au lieu d'être en doublon au-dessous »).
     Badgé, prévu, retenu, état — et, à l'écran seulement, Corriger / Valider. */
  /* LA JOURNÉE EN DEUX MORCEAUX (RQ, 30/09/2026) : le POINTAGE sous « ce qui
     s'est passé », l'ÉTAT et les boutons dans la colonne « À faire ». */
  const pointageDu = (date: string, type?: string) => {
    const j = actions?.journee(date);
    if (!j) return null;
    const toutes = vacationsDe(j);
    /* SEULE LA VACATION CONCERNÉE (RQ, 30/09/2026) : le retard se mesure sur
       la PREMIÈRE vacation prévue, le départ anticipé sur la DERNIÈRE, l'oubli
       sur celle(s) à qui il manque un badge. Les autres sont nommées sur la
       ligne « Journée », dont le total reste affiché : c'est lui qu'on valide. */
    const parDebut = toutes.map((v, i) => ({ v, i, deb: j.segments?.[i]?.attenduDebut || '' })).filter((x) => x.deb).sort((a, b) => a.deb.localeCompare(b.deb));
    const idx: number[] = toutes.length < 2 ? [] : type === 'retard' ? parDebut.slice(0, 1).map((x) => x.i)
      : type === 'depart' ? parDebut.slice(-1).map((x) => x.i)
        : type === 'oubli' ? toutes.map((v, i) => (v.manque ? i : -1)).filter((i) => i >= 0) : [];
    const vacs = idx.length ? idx.map((i) => toutes[i]!) : toutes;
    const autres = idx.length ? toutes.filter((_, i) => !idx.includes(i)).map((v) => libellePoste(v.poste)) : [];
    const retenu = j.retenuEffectif != null ? h(j.retenuEffectif) : j.heuresRetenues != null ? h(j.heuresRetenues) : '—';
    return (
      <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>
        {vacs.map((v, i) => (
          <div key={i}>
            {toutes.length > 1 && <strong style={{ fontWeight: 600 }}>{libellePoste(v.poste)} : </strong>}
            badgé {v.badge || '—'}{v.heures != null && ` (${h(v.heures)})`}
            {v.prevu && <> · prévu {v.prevu}{v.heuresPrevues != null && ` (${h(v.heuresPrevues)})`}</>}
          </div>
        ))}
        {!vacs.length && <div>badgé —</div>}
        <div>{toutes.length > 1 ? `Journée${autres.length ? ` (+ ${autres.join(', ')})` : ''} : ` : ''}prévu {h(j.heuresPlanifiees)} · retenu {retenu}</div>
      </div>
    );
  };
  /* VALIDER LE TEMPS EN PLUS (RQ, 30/09/2026) — exceptionnel et argumenté :
     heures (tout le surplus par défaut) + motif. */
  const heuresSupDu = (date: string) => {
    const j = actions?.journee(date);
    if (!j || j.validee || j.corrigePar || !actions?.onHeuresSup) return null;
    const total = j.surplus?.total || 0;
    if (!total) return null;
    if (supOuvert !== date) {
      return (
        <Bouton taille="sm" type="button" data-ecran-seulement onClick={() => { setSupOuvert(date); setSupHeures(String(j.heuresSupValidees || total).replace('.', ',')); setSupMotif(j.motifHeuresSup || ''); }} style={{ marginTop: 4 }}>
          {j.heuresSupValidees ? 'Modifier les heures sup' : 'Valider en heures sup'}
        </Bouton>
      );
    }
    return (
      <div data-ecran-seulement style={{ display: 'grid', gap: 4, marginTop: 6, minWidth: 220 }}>
        <label style={{ fontSize: 11.5 }}>Heures sup (max {h(total)})
          <input type="text" inputMode="decimal" value={supHeures} onChange={(e) => setSupHeures(e.target.value)} style={{ ...inputStyle, padding: '2px 6px', fontSize: 12 }} />
        </label>
        <textarea rows={2} value={supMotif} onChange={(e) => setSupMotif(e.target.value)} placeholder="Motif : qui l’a demandé, pourquoi (10 caractères min.)"
          style={{ ...inputStyle, fontSize: 12 }} />
        <span style={{ display: 'flex', gap: 4 }}>
          <Bouton variante="primaire" taille="sm" type="button" disabled={supMotif.trim().length < 10 && parseFloat(supHeures.replace(',', '.')) > 0} onClick={() => { actions!.onHeuresSup!(date, parseFloat(supHeures.replace(',', '.')) || 0, supMotif.trim()); setSupOuvert(null); }}>Enregistrer</Bouton>
          <Bouton taille="sm" type="button" onClick={() => setSupOuvert(null)}>Annuler</Bouton>
        </span>
      </div>
    );
  };
  const aFaire = (date: string, type?: string) => {
    const j = actions?.journee(date);
    if (!j) return null;
    const bloq = actions!.bloquee(j);
    /* « Prévalidée » (RQ, 01/11/2026) : l'enchaînement présumé n'est plus à
       confirmer — on le dit, au lieu d'un « à valider » qui réclame un geste. */
    const prevalidee = !bloq && (j.anomalies || []).includes('enchainement_presume');
    const etat = j.validee ? '✓ validée' : j.corrigePar ? '✓ corrigée' : bloq ? 'à corriger' : prevalidee ? '✓ prévalidée' : 'à valider';
    /* PLANIFIÉ, AUCUN POINTAGE : on déclare l'absence ici, avec son motif ; la
       journée est recalculée aussitôt (01/10/2026). */
    if (!j.validee && j.etat === 'absence_non_pointee' && actions?.onDeclarerAbsence) {
      return absOuvert === date ? (
        <div data-ecran-seulement style={{ display: 'grid', gap: 4 }}>
          <select value={absMotif} onChange={(e) => setAbsMotif(e.target.value)} style={{ ...inputStyle, fontSize: 12, padding: '2px 4px' }}>
            <option value="">— motif —</option>
            {(actions.motifsAbsence || []).map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
          </select>
          <span style={{ display: 'flex', gap: 4 }}>
            <Bouton variante="primaire" taille="sm" type="button" disabled={!absMotif || absEnvoi} onClick={() => void (async () => { setAbsEnvoi(true); try { await actions.onDeclarerAbsence!(date, absMotif); setAbsOuvert(null); } finally { setAbsEnvoi(false); } })()}>
              {absEnvoi ? '…' : 'Enregistrer'}
            </Bouton>
            <Bouton taille="sm" type="button" onClick={() => setAbsOuvert(null)}>Annuler</Bouton>
          </span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--red-600)' }}>absente sans motif</span>
          <span data-ecran-seulement style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Bouton variante="primaire" taille="sm" type="button" onClick={() => { setAbsMotif(''); setAbsOuvert(date); }}>Déclarer l’absence</Bouton>
            <Bouton taille="sm" type="button" onClick={() => actions.onCorriger(date)}>Corriger</Bouton>
          </span>
        </div>
      );
    }
    /* Journée validée : « Voir » à côté de l'état, sur la même ligne (01/10/2026). */
    if (j.validee) {
      return (
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--green-600)' }}>{etat}</span>
          <Bouton taille="sm" type="button" data-ecran-seulement onClick={() => actions!.onCorriger(date)}>Voir</Bouton>
        </span>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: j.validee || j.corrigePar || prevalidee ? 'var(--green-600)' : bloq ? 'var(--red-600)' : 'var(--text-body)' }}>{etat}</span>
        {/* Un retard seul se valide ; s'il est bloqué, c'est par un AUTRE point
            du même jour (oubli de badgeage de la garde…), et il faut le dire. */}
        {bloq && !j.validee && (type === 'retard' || type === 'plus' || type === 'alheure') && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>bloquée par l’autre point du même jour — à corriger d’abord</span>
        )}
        {/* Un RETARD n'est pas une anomalie (RQ, 30/09/2026) : la journée se
            valide telle quelle, Valider vient donc en premier. Corriger ne sert
            qu'à un oubli de badge signalé ou à des heures à reprendre. */}
        <span data-ecran-seulement style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {!j.validee && (
            <Bouton variante={bloq ? 'contour' : 'primaire'} taille="sm" type="button" disabled={bloq} title={bloq ? 'Pointage incomplet : corrigez d’abord les heures retenues' : 'Valider cette journée telle quelle (retard compris)'} onClick={() => actions!.onValider(date)}>Valider</Bouton>
          )}
          <Bouton taille="sm" type="button" onClick={() => actions!.onCorriger(date)}>
            {j.validee ? 'Voir' : 'Corriger'}
          </Bouton>
        </span>
      </div>
    );
  };
  /* GARDE OU LENDEMAIN DE GARDE, à côté du jour (RQ, 30/09/2026) : un retard
     ou un oubli ne se lit pas pareil un jour de garde ou au retour d'une nuit.
     La journée le sait (RH) ; à défaut, les postes de la ligne. */
  const jourAvecGarde = (date: string, postes: string[] = []) => {
    const j = actions?.journee(date);
    const garde = (j?.postes || postes).some((x) => /^GARDE/.test(String(x)));
    const apres = !!j?.apresGarde;
    return (
      <>
        {JOUR_LONG(date)}
        {garde && <span style={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>GARDE</span>}
        {apres && <span style={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)' }}>POST GARDE</span>}
      </>
    );
  };
  /* Les journées à traiter qu'aucune rubrique ci-dessous ne nomme
     (enchaînement à confirmer, absence non pointée…). */
  const nommees = new Set([...d.retards.map((x) => x.date), ...d.departsAnticipes.map((x) => x.date), ...d.oublis.map((x) => x.date), ...d.sens.map((x) => x.date)]);
  return (
    <>
    <h2 style={{ margin: '0 0 2px', fontSize: 16 }}>{d.nom} — pointage de {libMois}</h2>
    <p className="muet" style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 10px' }}>
      Relevé des faits enregistrés par la badgeuse, pour information et sensibilisation. Ce document n’est pas une sanction.
    </p>

    {/* LE POSITIF D'ABORD (RQ, 30/09/2026) — une fiche de sensibilisation
        qui ne listerait que les manquements se lirait comme un reproche. */}
    <h3 style={{ fontSize: 13.5, margin: '12px 0 4px' }}>Bilan du mois <span className="muet" data-ecran-seulement style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>— passez la souris sur un libellé pour son explication</span></h3>
    <table ref={enTeteCollant} className="ged-tableau" style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        <tr><td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Heures attendues']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Heures attendues</span></td><td style={{ ...td, fontWeight: 700 }}>{h(d.bilan.heuresAttendues)}</td>
          <td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Heures réalisées']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Heures réalisées</span> <span className="muet" style={{ fontSize: 11, color: 'var(--text-muted)' }}>(extras compris)</span></td><td style={{ ...td, fontWeight: 700 }}>{h(d.bilan.heuresRealisees)}</td>
          <td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Écart']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Écart</span></td><td style={{ ...td, fontWeight: 700, color: d.bilan.ecart < 0 ? 'var(--red-600)' : 'var(--green-600)' }}>
            {d.bilan.ecart > 0 ? '+' : d.bilan.ecart < 0 ? '−' : ''}{h(Math.abs(d.bilan.ecart))}</td></tr>
        {d.bilan.heuresRetenues != null && (
          <tr><td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Heures retenues']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Heures retenues</span> <span className="muet" style={{ fontSize: 11, color: 'var(--text-muted)' }}>(payées)</span></td><td style={{ ...td, fontWeight: 700 }}>{h(d.bilan.heuresRetenues)}</td>
            <td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Surplus (non heures sup)']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Surplus (non heures sup)</span></td><td style={{ ...td, fontWeight: 700, color: d.bilan.surplusNonValide ? 'var(--amber-600)' : undefined }}>{h(d.bilan.surplusNonValide || 0)}</td>
            <td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Heures sup validées']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Heures sup validées</span></td><td style={{ ...td, fontWeight: 700, color: d.bilan.heuresSupValidees ? 'var(--green-600)' : undefined }}>{h(d.bilan.heuresSupValidees || 0)}</td></tr>
        )}
        <tr><td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Jours comptés']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Jours comptés</span></td><td style={td}>{d.bilan.joursMesures}</td>
          <td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Arrivées à l’heure']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Arrivées à l’heure</span></td><td style={td}>{d.arriveesALHeure} sur {d.joursTravailles} jours travaillés</td>
          <td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Total des retards']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Total des retards</span></td><td style={{ ...td, fontWeight: 700, color: d.retards.length ? 'var(--red-600)' : undefined }}>
            {h(d.retards.reduce((n, r) => n + r.minutes, 0) / 60)}{d.retards.length ? ` (${d.retards.length} retard${d.retards.length > 1 ? 's' : ''})` : ''}</td></tr>
        <tr><td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Jours attendus (planning)']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Jours attendus (planning)</span></td><td style={{ ...td, fontWeight: 700 }}>{d.jours.attendus}</td>
          <td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Jours validés']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Jours validés</span></td><td style={{ ...td, fontWeight: 700, color: 'var(--green-600)' }}>{d.jours.valides}</td>
          <td style={{ ...td, cursor: 'help' }} title={AIDE_BILAN['Jours non validés']}><span style={{ borderBottom: '1px dotted var(--text-muted)' }}>Jours non validés</span></td><td style={{ ...td, fontWeight: 700 }}>{d.jours.nonValides}</td></tr>
      </tbody>
    </table>
    <p className="muet" style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '3px 0 0' }}>
      Jours comptés : mesurés par la badgeuse, hors absences. Une journée avec un oubli de badgeage non régularisé
      n’entre ni dans l’attendu ni dans le réalisé : elle y reviendra dès qu’elle sera corrigée.
    </p>
    {actions && (
      <div data-ecran-seulement style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '10px 0 0' }}>
        <Bouton variante="primaire" taille="sm" type="button" disabled={d.jours.nonValides === 0} onClick={actions.onValiderMois}>
          Valider tout le mois ({d.jours.nonValides} journée(s))
        </Bouton>
        {actions.onValiderSelection && (
          <>
            <Bouton variante="primaire" taille="sm" type="button" disabled={!cochees.length || envoiSelection}
              title="Valider seulement les journées cochées dans « Jour par jour », et laisser les autres"
              onClick={() => void (async () => {
                setEnvoiSelection(true);
                try { await actions.onValiderSelection!(cochees); setChoisies(new Set()); } finally { setEnvoiSelection(false); }
              })()}>
              {envoiSelection ? 'Validation…' : `Valider la sélection (${cochees.length})`}
            </Bouton>
            <Bouton taille="sm" type="button" disabled={!validables.size}
              onClick={() => setChoisies(cochees.length === validables.size ? new Set() : new Set(validables))}>
              {cochees.length === validables.size && validables.size ? 'Tout décocher' : `Tout cocher (${validables.size})`}
            </Bouton>
          </>
        )}
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Les journées à pointage incomplet doivent être corrigées d’abord ; chaque anomalie ci-dessous porte ses boutons.</span>
      </div>
    )}

    {/* LES POINTS D'ATTENTION, DANS L'ORDRE DES JOURS (RQ, 30/09/2026 : « au
        lieu de séparer par type, par ordre de date, avec une pastille colorée
        pour le type »). Une ligne par fait ; une journée peut en porter deux. */}
    {(() => {
      type Point = { date: string; type: keyof typeof TYPES_POINT; detail: string; suite: string; ordre: number; poste: string };
      /* Le poste concerné, lu sur les vacations de la journée (RH) :
         retard = la première prise de poste ; départ = le dernier poste de
         jour ; erreur de sens = la vacation qui contient l'heure ; oubli = le
         poste nommé par la vacation incomplète. À défaut (« Mes présences »),
         les postes planifiés de la ligne. */
      const segsDu = (date: string) => (actions?.journee(date)?.segments || []).filter((x) => x.poste);
      const posteRetard = (date: string, repli: string[]) => {
        const sg = segsDu(date).slice().sort((x, y) => String(x.attenduDebut || '').localeCompare(String(y.attenduDebut || '')))[0];
        return sg?.poste ? libellePoste(sg.poste) : (repli[0] || '');
      };
      const posteDepart = (date: string, repli: string[]) => {
        const sg = segsDu(date).filter((x) => x.poste !== 'GARDE').sort((x, y) => String(y.attenduFin || '').localeCompare(String(x.attenduFin || '')))[0];
        return sg?.poste ? libellePoste(sg.poste) : (repli[repli.length - 1] || '');
      };
      const posteHeure = (date: string, heure: string) => {
        const ts = `${date}T${heure}:00`;
        const sg = segsDu(date).find((x) => x.attenduDebut && x.attenduFin && ts >= x.attenduDebut && ts <= x.attenduFin) || segsDu(date)[0];
        return sg?.poste ? libellePoste(sg.poste) : '';
      };
      const postesJour = (date: string) => [...new Set(segsDu(date).map((x) => libellePoste(x.poste!)))].join(', ');
      /* L'oubli nomme son poste entre parenthèses : on le sort du texte vers la colonne. */
      const posteOubli = (manque: string) => [...new Set([...manque.matchAll(/\(([A-Z0-9_]+)\)/g)].map((m) => libellePoste(m[1]!)))].join(', ');
      const sansPoste = (txt: string) => txt.replace(/\s*\([A-Z0-9_]+\)/g, '');
      const points: Point[] = [
        ...d.retards.map((r) => ({ date: r.date, type: 'retard' as const, ordre: 1,
          detail: `arrivée ${r.arrivee?.slice(11, 16) || '—'} · ${h(r.minutes / 60)} de retard`, suite: '', poste: posteRetard(r.date, r.postes) })),
        ...d.departsAnticipes.map((x) => ({ date: x.date, type: 'depart' as const, ordre: 2,
          detail: `départ ${x.depart.slice(11, 16)} au lieu de ${x.finPrevue.slice(11, 16)} · ${h(x.minutes / 60)} plus tôt`, suite: '', poste: posteDepart(x.date, x.postes) })),
        ...d.oublis.map((o) => ({ date: o.date, type: 'oubli' as const, ordre: 3, detail: sansPoste(o.manque), suite: o.corrigee ? 'régularisé' : '',
          poste: posteOubli(o.manque) || o.postes.join(', ') })),
        ...d.sens.map((x) => ({ date: x.date, type: 'sens' as const, ordre: 4,
          detail: `${x.heure} · « ${x.de} » choisi au lieu de « ${x.vers} »`, suite: x.statut === 'corrige' ? 'corrigé' : 'probable, à vérifier', poste: posteHeure(x.date, x.heure) })),
        ...(actions ? (d.joursATraiter || []).filter((x) => !nommees.has(x.date))
          .map((x) => ({ date: x.date, type: 'autre' as const, ordre: 5, detail: x.raison, suite: '', poste: postesJour(x.date) })) : []),
        /* LE POSITIF, DANS LE MÊME TABLEAU (RQ, 30/09/2026 : « rajoute tous les
           jours, avec une pastille Temps en plus et une pastille À l'heure »). */
        ...d.plus.map((x) => ({ date: x.date, type: 'plus' as const, ordre: 6,
          detail: `prévu ${h(x.prevu)} · réalisé ${h(x.realise)} · +${h(x.enPlus)}`,
          suite: x.valide ? `${h(x.valide)} validée(s) en heures sup` : '', poste: postesJour(x.date) })),
        ...d.exemplaires.map((x) => ({ date: x.date, type: 'alheure' as const, ordre: 7,
          detail: `${x.arrivee?.slice(11, 16) || '—'} → ${x.depart?.slice(11, 16) || '—'} · ${h(x.heures)}`, suite: '', poste: postesJour(x.date) })),
      ];
      /* L'OUBLI CORRIGÉ D'UN JOUR À L'HEURE (RQ, 01/10/2026 — Ons le 29/09 :
         « l'oubli apparaît en plus de la ligne À l'heure »). Une fois l'heure
         réelle déclarée, la journée EST à l'heure : une seule ligne, À l'heure,
         avec une pastille « Oubli corrigé » qui garde la trace de ce qui a été
         déclaré. Un oubli NON corrigé garde toujours sa propre ligne. */
      /* 01/10/2026 : « pareil pour les retards » — l'oubli corrigé se range
         aussi sous la ligne Retard du même jour. */
      const hotes = new Set(points.filter((x) => x.type === 'alheure' || x.type === 'retard').map((x) => x.date));
      const oubliCorrige = new Map<string, string>();
      for (let k = points.length - 1; k >= 0; k--) {
        const x = points[k]!;
        if (x.type === 'oubli' && x.suite === 'régularisé' && hotes.has(x.date)) {
          oubliCorrige.set(x.date, x.detail);
          points.splice(k, 1);
        }
      }
      /* Les jours que rien ne nomme : absence, ou journée sans remarque. */
      const vus = new Set(points.map((x) => x.date));
      for (const j of actions?.journees || []) {
        if (vus.has(j.date) || j.etat === 'non_mesure') continue;
        points.push(j.etat === 'absence_justifiee'
          ? { date: j.date, type: 'absence' as const, ordre: 8, detail: 'absence enregistrée (congé, maladie…)', suite: '', poste: '' }
          : { date: j.date, type: 'rien' as const, ordre: 9, detail: `${h(j.heuresRetenues ?? j.heuresPointees)} pour ${h(j.heuresPlanifiees)} prévues`, suite: '', poste: postesJour(j.date) });
      }
      points.sort((p1, p2) => p1.date.localeCompare(p2.date) || p1.ordre - p2.ordre);
      const plieOubli = (x: { type: string; date: string }) => (x.type === 'alheure' || x.type === 'retard') && oubliCorrige.has(x.date);
      const compte = (t: string) => points.filter((x) => x.type === t || (t === 'oubli' && plieOubli(x))).length;
      const valideeLe = (date: string) => !!actions?.journee(date)?.validee;
      const nbValidees = actions ? new Set(points.filter((x) => valideeLe(x.date)).map((x) => x.date)).size : 0;
      const masquees = !!actions && !voirValidees;
      const vues = (filtre.length ? points.filter((x) => filtre.includes(x.type) || (filtre.includes('oubli') && plieOubli(x))) : points)
        .filter((x) => !masquees || !valideeLe(x.date));
      return (
        <>
          <h3 style={{ fontSize: 13.5, margin: '18px 0 4px' }}>Jour par jour ({vues.length}{filtre.length || (masquees && nbValidees) ? ` sur ${points.length}` : ''}){masquees && nbValidees > 0 && <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--text-muted)' }}> — validées masquées</span>}</h3>
          <p style={{ fontSize: 12, margin: '0 0 6px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {(Object.keys(TYPES_POINT) as (keyof typeof TYPES_POINT)[]).filter((t) => compte(t)).map((t) => (
              <span key={t}><PastilleType type={t} actif={filtre.includes(t)} onClick={() => basculerFiltre(t)} /> {compte(t)}</span>
            ))}
            {filtre.length > 0 && (
              <Bouton taille="sm" type="button" data-ecran-seulement onClick={() => setFiltre([])}>Tout afficher</Bouton>
            )}
            {nbValidees > 0 && (
              <Bouton taille="sm" type="button" data-ecran-seulement aria-pressed={voirValidees} onClick={() => setVoirValidees((v) => !v)}>
                {voirValidees ? `Masquer les journées validées (${nbValidees})` : `Afficher les journées validées (${nbValidees})`}
              </Bouton>
            )}
          </p>
          {/* L10 (11/10/2026) : le jour par jour est un registre SIMPLE sur le socle ; « À faire » est la colonne d'actions. */}
          <Tableau
            id="rh-fiche-jours"
            niveau="simple"
            densite="compacte"
            lignes={vues.map((x, i) => ({ ...x, cle: `${x.date}-${x.type}-${i}` }))}
            cleLigne={(x) => x.cle}
            vide={{ titre: filtre.length ? 'Aucun jour de ce type.' : masquees && nbValidees ? 'Toutes les journées sont validées.' : 'Aucune journée ce mois-ci.' }}
            colonnes={[
              ...(actions?.onValiderSelection ? [{
                cle: 'choix', libelle: '✓', valeur: (x: { date: string }) => (choisies.has(x.date) ? 1 : 0), style: { width: 28 },
                rendu: (x: { date: string }) => (validables.has(x.date) ? (
                  <span data-ecran-seulement>
                    <input type="checkbox" checked={choisies.has(x.date)} onChange={() => basculerChoix(x.date)}
                      aria-label={`Sélectionner le ${fmtDate(x.date)} pour la validation`} style={{ margin: 0, cursor: 'pointer' }} />
                  </span>
                ) : null),
              }] : []),
              { cle: 'date', libelle: 'Jour', type: 'date', valeur: (x) => x.date, style: { whiteSpace: 'nowrap' }, rendu: (x) => jourAvecGarde(x.date) || fmtDate(x.date) },
              {
                cle: 'type', libelle: 'Type · poste', type: 'liste', valeur: (x) => x.type, libelleValeur: (v) => TYPES_POINT[v as keyof typeof TYPES_POINT]?.libelle || v,
                rendu: (x) => (
                  <>
                    <PastilleType type={x.type} actif={filtre.includes(x.type)} onClick={() => setFiltre([x.type])} />
                    {plieOubli(x) && (
                      <span title={`Oubli de badgeage corrigé : ${oubliCorrige.get(x.date)}`}
                        style={{ display: 'inline-block', marginInlineStart: 4, fontSize: 10.5, fontWeight: 600, padding: '0 6px', borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--green-500)', color: 'var(--green-600)', background: 'var(--green-50)' }}>Oubli corrigé</span>
                    )}
                    {x.poste && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{x.poste}</div>}
                  </>
                ),
              },
              {
                cle: 'detail', libelle: 'Ce qui s’est passé', valeur: (x) => `${x.detail}${x.suite ? ` — ${x.suite}` : ''}`,
                rendu: (x) => (
                  <>
                    {x.detail}
                    {plieOubli(x) && <div style={{ fontSize: 11.5, color: 'var(--green-600)' }}>oubli corrigé : {oubliCorrige.get(x.date)}</div>}
                    {x.suite && <span style={{ marginInlineStart: 6, fontSize: 11.5, color: /^(régularisé|corrigé)$|validée/.test(x.suite) ? 'var(--green-600)' : /non payé/.test(x.suite) ? 'var(--amber-600)' : 'var(--text-muted)' }}>— {x.suite}</span>}
                    {pointageDu(x.date, x.type)}
                  </>
                ),
              },
            ]}
            actions={actions ? (x) => <>{aFaire(x.date, x.type)}{x.type === 'plus' && heuresSupDu(x.date)}</> : undefined}
          />
          {d.retardsToleres.length > 0 && (
            <p className="muet" style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Arrivées tardives tolérées par décision de la RQ, non comptées : {d.retardsToleres.map((r) => `${JOUR_LONG(r.date)} (${r.arrivee?.slice(11, 16) || '—'})`).join(', ')}.
            </p>
          )}
        </>
      );
    })()}
    </>
  );
}

/**
 * LA FICHE D'UNE PERSONNE POUR LE MOIS (RQ, 30/09/2026 : « voir les dates des
 * retards, des absences de badgeage et des erreurs de sens — dans un but de
 * sensibilisation »). Des faits datés, avec ce que l'outil en a déjà fait ;
 * imprimable pour être remise à la personne.
 */
function FichePersonne({ mois, orgId, onFermer }: { mois: string; orgId: string; onFermer: () => void }) {
  const charger = useGed((s) => s.loadDetailPresences);
  const chargerJournees = useGed((s) => s.loadJourneesPersonne);
  const corrigerJournee = useGed((s) => s.corrigerJournee);
  const rouvrirJournee = useGed((s) => s.rouvrirJournee);
  const validerPresences = useGed((s) => s.validerPresences);
  const validerHeuresSup = useGed((s) => s.validerHeuresSup);
  const loadRecap = useGed((s) => s.loadRecapPresences);
  const showToast = useGed((s) => s.showToast);
  /* Les motifs d'absence du paramétrage, pour « Déclarer l'absence ». */
  const [motifsAbsence, setMotifsAbsence] = React.useState<{ code: string; label: string }[]>([]);
  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/parametres', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null))
      .then((x) => { if (vivant && x) setMotifsAbsence(((x.parametres?.motifsAbsence || []) as { code: string; label: string }[]).map((m) => ({ code: m.code, label: m.label }))); })
      .catch(() => { /* sans motifs, le bouton reste inactif */ });
    return () => { vivant = false; };
  }, []);
  const [d, setD] = React.useState<Detail | null>(null);
  /* LES JOURNÉES DU MOIS, pour corriger et valider DEPUIS LA FICHE (RQ,
     30/09/2026 : « je vais les valider avec eux devant l'ordinateur »). */
  const [journees, setJournees] = React.useState<RhJournee[] | null>(null);
  const [ouverte, setOuverte] = React.useState<RhJournee | null>(null);
  const [dm, fm] = (() => { const [y, mo] = mois.split('-').map(Number); return [`${mois}-01`, `${mois}-${String(new Date(y, mo, 0).getDate()).padStart(2, '0')}`]; })();
  const recharger = React.useCallback(async () => {
    const [x, js] = await Promise.all([charger(mois, orgId), chargerJournees(dm, fm, orgId)]);
    setD(x as Detail | null); setJournees(js.sort((p1, p2) => p1.date.localeCompare(p2.date)));
  }, [charger, chargerJournees, mois, orgId, dm, fm]);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- efface la fiche précédente pendant la requête
  React.useEffect(() => { setD(null); setJournees(null); void recharger(); }, [recharger]);
  const apres = async () => { await recharger(); void loadRecap(mois); };
  /* « Planifié, aucun pointage » BLOQUE aussi (RQ, 01/10/2026 : « il faut que
     ce soit écrit corriger ; ça ne peut pas se valider directement ») : on
     déclare l'absence, ou l'on corrige. */
  const bloquee = (j: RhJournee) => (j.etat === 'anomalie' || j.etat === 'absence_non_pointee') && j.heuresRetenues == null && !j.corrigePar;
  const ouvrirJour = (date: string) => { const j = (journees || []).find((x) => x.date === date); if (j) setOuverte(j); };
  const [a, m] = mois.split('-').map(Number);
  const libMois = libelleMois(`${a}-${String(m).padStart(2, '0')}`);
  const ref = React.useRef<HTMLDivElement>(null);
  /* ⚠️ PAS DE NOUVELLE FENÊTRE (30/09/2026) : `window.open` est bloqué par
     les navigateurs qui filtrent les fenêtres surgissantes, et le bouton ne
     faisait alors RIEN, sans un mot. On imprime depuis un cadre invisible de
     la page elle-même. */
  const imprimer = () => {
    if (!ref.current) return;
    const cadre = document.createElement('iframe');
    cadre.setAttribute('aria-hidden', 'true');
    Object.assign(cadre.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
    document.body.appendChild(cadre);
    const w = cadre.contentWindow;
    if (!w) { cadre.remove(); return; }
    w.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Pointage — ${d?.nom || ''} — ${libMois}</title>
      <style>body{font-family:system-ui,sans-serif;font-size:12px;margin:24px;color:#111}h2{font-size:16px}h3{font-size:13px;margin:16px 0 4px}
      table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:3px 6px;text-align:left}button,[data-ecran-seulement]{display:none}.muet{color:#666}</style></head>
      <body>${ref.current.innerHTML}</body></html>`);
    w.document.close();
    const retirer = () => setTimeout(() => cadre.remove(), 500);
    w.addEventListener('afterprint', retirer);
    setTimeout(() => { w.focus(); w.print(); setTimeout(() => { if (document.body.contains(cadre)) cadre.remove(); }, 60000); }, 50);
  };
  return (
    <div style={{ ...card, padding: 16, margin: '12px 0', borderInlineStart: '3px solid var(--color-primary-active)' }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Bouton taille="sm" type="button" onClick={imprimer} disabled={!d}><I n="print" s={13} /> Imprimer la fiche</Bouton>
        <Bouton taille="sm" type="button" onClick={onFermer} aria-label="Fermer la fiche">✕</Bouton>
      </div>
      {!d ? <Skeleton rows={5} /> : (
        <div ref={ref}>
          <FicheReleve libMois={libMois}
            d={{
              ...d,
              /* Les journées à traiter qu'aucune rubrique ne nomme : pointage
                 incomplet (enchaînement à confirmer…) ou absence non pointée. */
              joursATraiter: (journees || []).filter((j) => !j.validee && (bloquee(j) || j.etat === 'absence_non_pointee'))
                .map((j) => ({ date: j.date, raison: j.etat === 'absence_non_pointee' ? 'planifié(e), aucun badge ce jour-là'
                  : (j.anomalies || []).map((x) => LIB_ANOMALIE[x] || x).join(' · ') || 'pointage incomplet' })),
            }}
            actions={journees ? {
              journee: (date) => journees.find((j) => j.date === date),
              journees,
              bloquee,
              onCorriger: ouvrirJour,
              onValider: (date) => { void (async () => { if (await validerPresences(date, date, false, undefined, orgId)) await apres(); })(); },
              onValiderMois: () => { void (async () => { if (await validerPresences(dm, fm, false, undefined, orgId)) await apres(); })(); },
              /* Une requête par journée cochée, sur la route qui valide déjà UN
                 jour : rien de neuf au serveur, et chaque refus reste nommé. */
              onValiderSelection: async (dates) => {
                let ok = 0; const refus: string[] = [];
                for (const date of dates) {
                  const r = await fetchEcriture('/api/ged/ged/rh/journees/valider', {
                    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ du: date, au: date, personnelOrgId: orgId }),
                  });
                  const x = await r.json().catch(() => ({}));
                  if (r.ok && Number(x?.validees) > 0) ok += 1;
                  else refus.push(`${fmtDate(date)} : ${x?.error?.message
                    || ((x?.ecartees || []).length ? 'modifiée pendant la validation, relisez-la' : r.ok ? 'rien à valider (déjà validée ?)' : 'refusée')}`);
                }
                await apres();
                showToast(`${ok} journée(s) validée(s).${refus.length ? ` ${refus.length} non validée(s) — ${refus.slice(0, 3).join(' · ')}${refus.length > 3 ? '…' : ''}` : ''}`,
                  refus.length ? 'erreur' : undefined);
              },
              onHeuresSup: (date, heures, motif) => { void (async () => {
                const j = journees.find((x) => x.date === date);
                if (j && await validerHeuresSup(j.documentId, heures, motif)) await apres();
              })(); },
              motifsAbsence,
              onDeclarerAbsence: async (date, motif) => {
                const r = await fetchEcriture('/api/ged/ged/rh/absences', {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ personnelOrgId: orgId, motif, dateDebut: date, dateFin: date }),
                });
                const x = await r.json().catch(() => ({}));
                if (!r.ok) { showToast(x?.error?.message || 'Absence refusée.', 'erreur'); return; }
                await fetchEcriture('/api/ged/ged/rh/journees/recalcul', {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ du: date, au: date }),
                });
                showToast('Absence enregistrée — la journée est recalculée.');
                await apres();
              },
            } : undefined} />
        </div>
      )}
      {ouverte && (
        <DialogueCorrection
          j={ouverte} onFermer={() => setOuverte(null)}
          veille={(journees || []).find((x) => x.date === isoJour(new Date(new Date(`${ouverte.date}T12:00:00Z`).getTime() - 86400000)))}
          onCorriger={async (heures, motif, reel) => { const ok = await corrigerJournee(ouverte.documentId, heures, motif, reel); if (ok) { setOuverte(null); await apres(); } }}
          onRouvrir={async () => { const ok = await rouvrirJournee(ouverte.documentId); if (ok) { setOuverte(null); await apres(); } }}
          peutRouvrir
        />
      )}
    </div>
  );
}

/**
 * LE RÉCAPITULATIF DU MOIS PAR PERSONNE (RQ, 30/09/2026) : « un tableau
 * récapitulatif pour chaque personne pour le mois » et « des stats : nombre
 * de retards, oublis de badgeage, erreurs du sens ». Trié par nom ; un clic
 * sur un en-tête trie par cette colonne.
 */
function RecapMois({ mois, onMois }: { mois: string; onMois: (m: string) => void }) {
  const rhRecap = useGed((s) => s.rhRecap);
  const loadRecap = useGed((s) => s.loadRecapPresences);
  const rhChargement = useGed((s) => s.rhChargement);
  /* L4 (10/10/2026) : le tri vit dans `<Tableau>` — fini le `<th onClick>`
     inaccessible au clavier et l'`aria-sort` absent des colonnes non triées
     (É-6, B-3). L'ordre PAR DÉFAUT reste le nom : c'est l'ordre des données. */
  /* La fiche ouverte : un clic sur un nom (30/09/2026). */
  const [fiche, setFiche] = React.useState<string | null>(null);
  const refFiche = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => { if (fiche) refFiche.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [fiche]);
  React.useEffect(() => { void loadRecap(mois); }, [mois, loadRecap]);
  const [a, m] = mois.split('-').map(Number);
  const libelle = libelleMois(`${a}-${String(m).padStart(2, '0')}`);
  const decaler = (n: number) => { const d = new Date(a, m - 1 + n, 1); onMois(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
  const lignes = (rhRecap && rhRecap.mois === mois ? rhRecap.lignes : null);
  const tries = lignes ? lignes.slice().sort((x, y) => String(x.nom ?? '').localeCompare(String(y.nom ?? ''), 'fr')) : [];
  const total = (cle: string, parmi: typeof tries) => Math.round(parmi.reduce((n, l) => n + Number(l[cle] || 0), 0) * 100) / 100;
  const fmt = (cle: string, v: unknown) => {
    const n = Number(v || 0);
    if (cle === 'heuresEnMoins') return n ? <span style={{ color: 'var(--red-600)' }}>−{h(n)}</span> : <span style={{ color: 'var(--text-faint)' }}>0</span>;
    if (cle === 'heuresEnPlus') return n ? <span style={{ color: 'var(--green-600)' }}>+{h(n)}</span> : <span style={{ color: 'var(--text-faint)' }}>0</span>;
    if (['heuresPrevues', 'heuresRetenues', 'heuresRealisees', 'heuresSupValidees', 'ecart'].includes(cle)) return h(n);
    if (cle === 'surplusNonValide') return n ? <span style={{ color: 'var(--amber-600)' }}>{h(n)}</span> : <span style={{ color: 'var(--text-faint)' }}>0</span>;
    return n === 0 ? <span style={{ color: 'var(--text-faint)' }}>0</span> : n;
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', flexWrap: 'wrap' }}>
        <Bouton taille="sm" onClick={() => decaler(-1)}><I n="chevron-left" s={14} /> Mois précédent</Bouton>
        <strong style={{ fontSize: 14 }}>{libelle}</strong>
        <Bouton taille="sm" onClick={() => decaler(1)}>Mois suivant <I n="chevron-right" s={14} /></Bouton>
      </div>
      {!lignes ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={8} /></div>
      ) : !tries.length ? (
        <EmptyState titre={`Rien à récapituler en ${libelle}`} indice="Aucun planning ni pointage pour ce mois." />
      ) : (
        <div style={{ ...card, padding: 0 }}>
          <Tableau
            id="rh-presences-recap"
            densite="compacte"
            defilementHorizontal
            lignes={tries}
            cleLigne={(l) => String(l.personne)}
            ligneProps={(l) => (String(l.personne) === fiche ? { style: { background: 'var(--blue-50)' } } : {})}
            colonnes={COLS_RECAP.map(([cle, lib, aide]) => (cle === 'nom'
              ? {
                cle, libelle: lib, aide: aide || undefined, epinglee: true, obligatoire: true,
                valeur: (l: (typeof tries)[number]) => String(l.nom ?? ''),
                style: { fontWeight: 600 },
                rendu: (l: (typeof tries)[number]) => (
                  <button type="button" onClick={() => setFiche(String(l.personne))}
                    style={{ background: 'none', border: 0, padding: 0, font: 'inherit', fontWeight: 600, color: 'var(--color-primary-active)', cursor: 'pointer', textAlign: 'start' }}
                    title="Voir le détail daté : retards, oublis, erreurs de sens">
                    {String(l.nom)}
                  </button>
                ),
              }
              : {
                cle, libelle: lib, aide: aide || undefined, type: 'nombre' as const,
                valeur: (l: (typeof tries)[number]) => Number(l[cle] || 0),
                style: { whiteSpace: 'normal' },
                rendu: (l: (typeof tries)[number]) => (
                  <span style={{
                    ...(cle === 'anomaliesOuvertes' && Number(l[cle]) > 0 ? { color: 'var(--red-600)', fontWeight: 700 } : {}),
                    ...(cle === 'joursNonValides' && Number(l[cle]) > 0 ? { fontWeight: 700 } : {}),
                  }}>
                    {fmt(cle, l[cle])}
                  </span>
                ),
              }))}
            /* Le pied compte les lignes FILTRÉES : un total qui ignorerait le filtre mentirait. */
            pied={(c, parmi) => (c.cle === 'nom' ? `Total (${parmi.length})` : fmt(c.cle, total(c.cle, parmi)))}
          />
        </div>
      )}
      {rhChargement > 0 && lignes && <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Mise à jour…</p>}
      {/* LA FICHE SOUS LE TABLEAU (RQ, 01/10/2026) : le tableau de tout le
          monde reste en place au-dessus ; on descend jusqu'à la fiche. */}
      {fiche && <div ref={refFiche} style={{ scrollMarginTop: 12 }}><FichePersonne mois={mois} orgId={fiche} onFermer={() => setFiche(null)} /></div>}
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
        Des comptes, pas un classement. Les journées non mesurées n’entrent dans aucun cumul d’heures ni d’absence.
        Survolez un en-tête pour sa définition ; cliquez pour trier. Cliquez sur un nom pour sa fiche détaillée.
      </p>
    </div>
  );
}

/** Bornes du mois qui contient ce jour. */
function moisDe(iso: string): { du: string; au: string; libelle: string } {
  const [a, m] = iso.split('-').map(Number);
  const fin = new Date(a, m, 0).getDate();
  const mm = String(m).padStart(2, '0');
  const libelle = libelleMois(`${a}-${String(m).padStart(2, '0')}`);
  return { du: `${a}-${mm}-01`, au: `${a}-${mm}-${String(fin).padStart(2, '0')}`, libelle };
}

/**
 * LE CONTRÔLE DU MOIS AFFICHÉ (29/09/2026). Les désaccords entre personnel,
 * planning, absences et passages : chaque registre était exact, c'est leur
 * désaccord qui faussait les chiffres — et aucun écran ne le montrait. Ouvert
 * d'office dès qu'un constat est à trancher.
 */
function PanneauControle({ mois, onJour }: { mois: { du: string; au: string; libelle: string }; onJour: (d: string) => void }) {
  const rhControle = useGed((s) => s.rhControle);
  if (!rhControle || rhControle.du !== mois.du) return null;
  const cs = rhControle.constats;
  const n = (g: ConstatPointage['gravite']) => cs.filter((c) => c.gravite === g).length;
  /* ⚠️ Sans aucun passage importé, zéro constat ne prouve RIEN : dire « tout
     concorde » serait une affirmation fausse (constaté le 29/09/2026 sur
     octobre, mois encore vide). */
  if (!rhControle.passagesLus) {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px' }}>
        Contrôle des données — {mois.libelle} : aucun passage de badge importé, rien à confronter.
      </p>
    );
  }
  if (!cs.length) {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--green-600)', margin: '0 0 12px' }}>
        <I n="check" s={13} /> Contrôle des données — {mois.libelle} : planning, absences, fiches et badges concordent
        ({rhControle.passagesLus} passages confrontés).
      </p>
    );
  }
  return (
    <details open={n('bloquant') > 0} style={{ ...card, padding: '12px 14px', marginBottom: 12, background: 'var(--gray-25)' }}>
      <summary style={{ cursor: 'pointer', fontSize: 13.5, fontWeight: 600 }}>
        Contrôle des données — {mois.libelle} :{' '}
        {[
          n('bloquant') ? `${n('bloquant')} à trancher` : '',
          n('attention') ? `${n('attention')} à vérifier` : '',
          n('info') ? `${n('info')} pour information` : '',
        ].filter(Boolean).join(' · ')}
      </summary>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 10px' }}>
        Ce que le planning, les absences, les fiches du personnel et le fichier de la badgeuse ne disent pas de la même
        façon. Rien n’est corrigé d’office : chaque constat disparaît dès que la donnée en cause est corrigée.
      </p>
      <ListeConstats constats={cs} onJour={onJour} />
    </details>
  );
}

const fmtJour = (iso: string) => { const [, m, j] = iso.split('-'); return `${j}/${m}`; };
/* `heuresLisibles` de la maison (25/08/2026) : ce formateur rendait « 1.3 h »
   — un décimal que personne ne convertit de tête. */
const h = heuresLisibles;

/**
 * LES FICHIERS DE BADGEUSE IMPORTÉS, et le moyen d'en annuler un (RQ,
 * 30/09/2026 : « je me suis trompée de fichier et je n'ai aucun bouton pour
 * annuler cet import »). L'annulation est réservée à la RQ, motivée, et
 * refusée par le moteur si une journée de la période est validée.
 */
function FichiersImportes() {
  const settings = useGed((s) => s.settings);
  const couvertures = useGed((s) => s.rhCouvertures);
  const loadCouvertures = useGed((s) => s.loadCouvertures);
  const annuler = useGed((s) => s.annulerImportPointage);
  const estRQ = useGed((s) => s.session.roles.includes('rq'));
  const [cible, setCible] = React.useState<string | null>(null);
  const [motif, setMotif] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  React.useEffect(() => { if (couvertures === null) void loadCouvertures(); }, [couvertures, loadCouvertures]);
  if (!couvertures) return null;
  const liste = [...couvertures].sort((x, y) => String(y.importeLe || '').localeCompare(String(x.importeLe || '')));
  return (
    <details open={liste.length > 0} style={{ ...card, padding: '8px 14px', margin: '10px 0' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Fichiers de badgeuse importés ({liste.length})</summary>
      {/* L10 (11/10/2026) : registre SIMPLE sur le socle ; l'annulation (RQ, motivée) est la colonne d'actions. */}
      <Tableau
        id="rh-imports-pointage"
        niveau="simple"
        densite="compacte"
        lignes={liste}
        cleLigne={(c) => c.lot || `${c.du}-${c.au}`}
        vide={{ titre: 'Aucun fichier importé.' }}
        colonnes={[
          { cle: 'du', libelle: 'Période', type: 'date', valeur: (c) => c.du || '', style: { whiteSpace: 'nowrap' }, rendu: (c) => `${fmtDate(c.du)} → ${fmtDate(c.au)}` },
          {
            cle: 'fichier', libelle: 'Fichier', valeur: (c) => c.fichier || '',
            rendu: (c) => <>{c.fichier || <span style={{ color: 'var(--text-faint)' }}>sans nom</span>}<div style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{c.lot}</div></>,
          },
          {
            cle: 'passages', libelle: 'Passages', type: 'nombre', valeur: (c) => c.nbRetenus ?? 0,
            rendu: (c) => `${c.nbRetenus ?? 0} retenus${c.nbDoublons ? ` · ${c.nbDoublons} doublons` : ''}${c.nbRejets ? ` · ${c.nbRejets} rejetés` : ''}`,
          },
          { cle: 'importeLe', libelle: 'Importé le', type: 'date', valeur: (c) => c.importeLe || '', rendu: (c) => `${c.importeLe ? fmtDateHeure(c.importeLe) : '—'}${c.importePar ? ` · ${userName(settings, c.importePar)}` : ''}` },
        ]}
        actions={estRQ ? (c) => (c.lot ? (cible === c.lot ? (
          <div style={{ display: 'grid', gap: 6, minWidth: 260 }}>
            <textarea rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif (ex. mauvais fichier importé)…"
              aria-label="Motif de l’annulation" style={{ ...inputStyle, fontSize: 12.5 }} />
            <span style={{ fontSize: 11.5, color: 'var(--amber-600)' }}>Les passages de ce fichier et les journées de la période seront effacés. Irréversible.</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <Bouton variante="danger" taille="sm" type="button" disabled={motif.trim().length < 10 || enCours} onClick={async () => { setEnCours(true); const ok = await annuler(c.lot!, motif.trim()); setEnCours(false); if (ok) { setCible(null); setMotif(''); } }}>
                {enCours ? 'Annulation…' : 'Confirmer l’annulation'}
              </Bouton>
              <Bouton taille="sm" type="button" onClick={() => { setCible(null); setMotif(''); }}>Garder</Bouton>
            </div>
          </div>
        ) : (
          <Bouton taille="sm" type="button" onClick={() => { setCible(c.lot!); setMotif(''); }}>Annuler cet import</Bouton>
        )) : null) : undefined}
      />
    </details>
  );
}

export function Presences() {
  const {
    rhAcces, session, rhJournees, rhSemaine, setRhSemaine, rapportImport,
    importerPointages, effacerRapportImport, corrigerJournee, validerPresences, rouvrirJournee,
    recalculerPresences, corrigerJourneesEnMasse,
    rhChargement, rhPointages, loadPointages,
    rhCouvertures, loadCouvertures,
  } = useGed();
  const [importer, setImporter] = React.useState(false);
  /* L'ONGLET (30/09/2026) : la semaine, ou le récapitulatif du mois. */
  const [onglet, setOnglet] = React.useState<'semaine' | 'recap'>('semaine');
  /* Le mois en cours ; les 4 premiers jours, le mois qui vient de finir —
     c'est celui qu'on récapitule. */
  const [moisRecap, setMoisRecap] = React.useState(() => {
    const n = new Date(); const d = new Date(n.getFullYear(), n.getMonth() - (n.getDate() < 5 ? 1 : 0), 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [brut, setBrut] = React.useState(false);
  const [corrige, setCorrige] = React.useState<RhJournee | null>(null);
  /** Mode « correction en masse » (12/08/2026) : le clic sur une case SÉLECTIONNE
   *  au lieu d'ouvrir la modale unitaire — quinze journées d'une panne de
   *  badgeuse se motivent en un geste, pas en quinze. */
  const [masse, setMasse] = React.useState(false);
  const [choisies, setChoisies] = React.useState<Set<string>>(new Set());
  const [masseDialogue, setMasseDialogue] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [portee, setPortee] = React.useState('tous');
  const settings = useGed((s) => s.settings);

  // « Mon équipe » se lit dans l'organigramme (`responsable` de chaque personne),
  // pas dans le module RH : c'est la même hiérarchie qui sert partout, et la
  // dupliquer ici en ferait deux vérités à tenir d'accord.
  // Le lien FONCTIONNEL est exclu : le rattachement pointillé (indépendance du
  // RQ) ne donne pas autorité sur les heures de quelqu'un.
  // ⚠️ Ce hook est AVANT le refus d'accès plus bas — le déplacer après ferait
  // varier le nombre de hooks entre deux rendus et planterait React.
  const monEquipe = React.useMemo(() => new Set(
    (settings?.gedUsers || [])
      .filter((u) => u.responsable === session.key && u.lien !== 'fonctionnel')
      .map((u) => u.id),
  ), [settings?.gedUsers, session.key]);

  /* ⚠️ Ce hook aussi est AVANT le refus d'accès : le déplacer après ferait
     varier le nombre de hooks entre deux rendus et planterait React.

     ⚠️ ET IL DÉPEND DE `rhAcces` (26/08/2026). `loadCouvertures` refuse de lire
     tant que les droits RH ne sont pas connus — or ils arrivent APRÈS le
     premier rendu. Avec la seule dépendance `loadCouvertures`, l'effet partait
     une fois, se heurtait au garde, et ne repassait jamais : la couverture
     restait `null`, et le bandeau ne s'affichait sur aucune semaine. Défaut
     trouvé à l'écran, pas à la relecture. */
  // Au seul rôle `rh` depuis le 04/10/2026 (RQ) : plus d'accès par `responsable`.
  const peutLireRh = Boolean(rhAcces?.acces);
  React.useEffect(() => { if (peutLireRh) void loadCouvertures(); }, [peutLireRh, loadCouvertures]);
  // Forcer une validation malgré des anomalies relève du RQ — le RQ agit
  // depuis sa propre session, ou un responsable s'authentifie pour ce seul
  // geste depuis le poste de qui valide (06/09/2026). `useAutorisationResponsable`
  // est un hook : appelé AVANT le retour anticipé plus bas, jamais après.
  const estRQ = session.roles.includes('rq');
  const authForcage = useAutorisationResponsable();
  const [forcageOuvert, setForcageOuvert] = React.useState(false);

  /* ⚠️ Le mois du contrôle se lit sur le JEUDI de la semaine : une semaine à
     cheval sur deux mois appartient au mois qui en porte le plus de jours. */
  const moisControle = moisDe(joursSemaine(rhSemaine)[3]);
  const loadControle = useGed((s) => s.loadControle);
  React.useEffect(() => {
    if (rhAcces?.acces) void loadControle(moisControle.du, moisControle.au);
  }, [rhAcces?.acces, moisControle.du, moisControle.au, loadControle]);
  const rhAccesErreur = useGed((s) => s.rhAccesErreur);

  /* ⚠️ FAUX REFUS AU CHARGEMENT (29/09/2026). Tant que les droits RH ne sont
     pas lus (`rhAcces === null`), l'écran affichait « Réservé au gestionnaire
     RH » à la gestionnaire RH elle-même — une affirmation, pas une attente. */
  if (rhAcces === null && !rhAccesErreur) {
    return <div style={{ ...card, padding: 0, overflow: 'hidden', marginTop: 20 }}><Skeleton rows={6} /></div>;
  }
  if (!rhAcces?.acces) return <AccesRefuseRh />;

  const jours = joursSemaine(rhSemaine);
  /* LA SEMAINE AFFICHÉE EST-ELLE MESURÉE ? (26/08/2026)
     `null` = la couverture n'est pas encore lue, et on ne dit RIEN : afficher
     « aucun fichier importé » pendant le chargement serait une alerte fausse
     une fois sur deux. Miroir de `estMesure` (backend/src/rh-pointage.ts) — la
     règle de prudence est la même : aucune couverture déclarée = tout est
     réputé mesuré, pour qu'une base qui n'en déclare pas encore ne bascule pas
     d'un coup. */
  const joursNonCouverts = rhCouvertures === null || rhCouvertures.length === 0 ? []
    : jours.filter((d) => !rhCouvertures.some((c) => d >= c.du && d <= c.au));
  const semaineNonCouverte = joursNonCouverts.length === jours.length;

  const tousGens = Array.from(new Map(rhJournees.filter((j) => j.personnel).map((j) => [j.personnel!.orgId, j.personnel!])).values());

  const qq = q.trim().toLowerCase();
  const gens = tousGens
    .filter((p) => portee !== 'equipe' || monEquipe.has(p.orgId))
    .filter((p) => !qq || p.nom.toLowerCase().includes(qq));
  const caseDe = (orgId: string, date: string) => rhJournees.find((j) => j.personnel?.orgId === orgId && j.date === date);

  const decaler = (n: number) => {
    const [a, m, j] = rhSemaine.split('-').map(Number);
    setRhSemaine(lundiDe(new Date(a, m - 1, j + n * 7)));
  };

  /* ⚠️ Une journée CORRIGÉE (heures retenues + motif + auteur) est traitée,
     même si son état calculé reste « anomalie » : la compter encore « à
     traiter » faisait croire qu'une décision prise n'avait pas eu lieu. */
  const aTraiter = rhJournees.filter((j) => !j.validee && !j.corrigePar && ['anomalie', 'absence_non_pointee'].includes(j.etat)).length;
  const nonValidees = rhJournees.filter((j) => !j.validee).length;

  return (
    <section>
      <TitrePage eyebrow="Plannings &amp; RH" titre="Présences"
        sousTitre="Ce que la badgeuse a enregistré, confronté à ce qui était planifié. Le pointage d’origine n’est jamais réécrit : une correction s’ajoute à côté, avec son motif et son auteur — c’est ce qui rend les heures transmises à la paie vérifiables." />

      {rhAcces?.acces && (
        <div role="tablist" style={{ display: 'flex', gap: 6, margin: '10px 0 4px' }}>
          <Segment ariaLabel="Section" valeur={onglet} onChange={setOnglet} options={([['semaine', 'Semaine'], ['recap', 'Récapitulatif du mois']] as const).map(([k, lib]) => ({ valeur: k, libelle: lib }))} />
        </div>
      )}
      {onglet === 'recap' && rhAcces?.acces ? <RecapMois mois={moisRecap} onMois={setMoisRecap} /> : (<>
      {rapportImport && <RapportImport onFermer={effacerRapportImport} />}
      {rhAcces?.acces && <FichiersImportes />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', flexWrap: 'wrap' }}>
        <Bouton taille="sm" onClick={() => decaler(-1)}><I n="chevron-left" s={14} /> Précédente</Bouton>
        <strong style={{ fontFamily: 'var(--font-sans)', fontSize: 14 }}>
          Semaine du {fmtJour(jours[0])} au {fmtJour(jours[6])}
        </strong>
        <Bouton taille="sm" onClick={() => decaler(1)}>Suivante <I n="chevron-right" s={14} /></Bouton>
        <Bouton taille="sm" onClick={() => setRhSemaine(lundiDe(new Date()))}>Cette semaine</Bouton>
        {/* Le brut se charge AU CLIC, pas au montage : une semaine de passages de
            badge pèse bien plus que les journées reconstituées, et neuf
            consultations sur dix ne le demandent pas. */}
        <Bouton variante={brut ? 'primaire' : 'contour'} taille="sm" aria-expanded={brut} onClick={() => { const o = !brut; setBrut(o); if (o) void loadPointages(); }}>
          <I n="clock" s={14} /> {brut ? 'Masquer le pointage brut' : 'Pointage brut'}
        </Bouton>
        {/* GED-15 c — `POST ged/rh/journees/recalcul`, sur la semaine AFFICHÉE.
            Les journées ne se reconstituent qu'à l'import d'un fichier de
            badgeuse : corriger le planning ou valider une absence après coup
            laissait l'écart figé sur un planifié qui n'existe plus, sans autre
            remède que de réimporter le fichier. Le geste est ici, à côté de la
            semaine dont il parle, et jamais sur une plage qu'on ne voit pas. */}
        {rhAcces?.acces && (
          <ConfirmButton
            label="Recalculer la semaine" icon="refresh" variant="outline"
            titre="Reconstitue le pointé, le planifié, l’écart, l’état et les anomalies de cette semaine. Les journées déjà validées sont sautées ; les corrections (heures retenues, motif, auteur) sont conservées."
            question={`Reconstituer les journées du ${fmtJour(jours[0])} au ${fmtJour(jours[6])} depuis les pointages, le planning et les absences validées ? Les journées déjà validées ne seront pas touchées, et les corrections déjà saisies sont conservées.`}
            onConfirm={() => void recalculerPresences(jours[0], jours[6])} />
        )}
        {rhAcces?.acces && (
          <Bouton variante={masse ? 'primaire' : 'contour'} taille="sm" aria-pressed={masse} onClick={() => { setMasse(!masse); setChoisies(new Set()); }}>
            <I n="check" s={14} /> {masse ? 'Quitter la correction en masse' : 'Corriger en masse'}
          </Bouton>
        )}
        {rhAcces?.acces && (
          <Bouton variante="primaire" onClick={() => setImporter(true)} style={{ marginInlineStart: 'auto' }}>
            <I n="plus" s={14} /> Importer un fichier de pointage
          </Bouton>
        )}
      </div>

      {/* ⚠️ LA BARRE DE SÉLECTION EST ICI, ET PAS PLUS BAS. Elle vivait dans la
          branche « il y a des présences cette semaine » : sur une semaine vide —
          et le registre ne couvre que janvier à avril — le mode s'activait, le
          mode d'emploi s'affichait, et AUCUN moyen d'appliquer n'existait. C'est
          le « le bouton reste sans effet » signalé par la RQ. Un mode qui
          s'allume sans rien offrir se lit comme un bouton mort. */}
      {/* ⚠️ CE BANDEAU EST GRIS, PAS ROUGE — et c'est l'essentiel (RQ, 26/08/2026).
          Il ne signale pas une faute : il dit que le laboratoire ne SAIT pas ce
          qui s'est passé cette semaine-là. Au 26/08/2026, 1 516 journées de mai
          à juillet se lisaient comme de l'absentéisme faute d'avoir importé les
          fichiers de badgeuse de ces mois. Peindre ce bandeau en rouge à côté
          des vraies absences referait exactement ce défaut, en plus poli. */}
      {semaineNonCouverte && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', marginBottom: 12,
          background: 'var(--gray-25)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)',
        }}>
          <I n="info" s={16} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-body)' }}>
              <strong>Aucun fichier de pointage n’a été importé pour cette période.</strong>{' '}
              Les journées planifiées de la semaine du {fmtJour(jours[0])} au {fmtJour(jours[6])} ne
              sont ni des présences ni des absences — elles ne sont pas mesurées, et n’entrent dans
              aucun cumul.
            </p>
            {rhAcces?.acces && (
              <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
                Importez le fichier de la badgeuse de cette période pour les faire entrer au registre.
              </p>
            )}
          </div>
          {rhAcces?.acces && (
            <Bouton taille="sm" onClick={() => setImporter(true)}>
              <I n="plus" s={13} /> Importer
            </Bouton>
          )}
        </div>
      )}

      {rhAcces?.acces && <PanneauControle mois={moisControle} onJour={setRhSemaine} />}
      {rhAcces?.acces && <PanneauSens mois={moisControle} onJour={setRhSemaine} />}

      {masse && (
        <>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px' }}>
            Cliquez les journées à corriger — les validées ne sont pas sélectionnables (rouvrez-les
            d’abord). Un seul motif s’appliquera à toute la sélection.
          </p>
          <div className="ged-barre-collante" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <strong style={{ fontSize: 12.5 }}>{choisies.size} journée(s) sélectionnée(s)</strong>
            <Bouton taille="sm" disabled={!choisies.size} onClick={() => setChoisies(new Set())}>
              Tout désélectionner
            </Bouton>
            <Bouton variante="primaire" taille="sm" disabled={!choisies.size} onClick={() => setMasseDialogue(true)}>
              Corriger la sélection…
            </Bouton>
          </div>
        </>
      )}

      {brut && <PointageBrut jours={jours} pointages={rhPointages} journees={rhJournees}
        chargement={rhChargement > 0} filtre={qq} portee={portee} equipe={monEquipe} />}

      {aTraiter > 0 && (
        <div style={{ ...card, padding: 20, background: 'var(--red-50)', border: '1px solid var(--red-500)', marginBottom: 12 }}>
          <strong style={{ fontSize: 13, color: 'var(--red-600)' }}>
            {aTraiter} journée(s) à traiter avant validation
          </strong>
          <p style={{ fontSize: 12.5, margin: '4px 0 0' }}>
            Pointage incomplet ou personne planifiée sans aucun pointage. Valider en l’état
            transmettrait à la paie des heures dont personne n’a rien dit.
          </p>
        </div>
      )}

      {/* La pastille « Mon équipe » n'apparaît que pour qui en a une : l'offrir
          à tout le monde donnerait un filtre qui ne renvoie jamais rien. */}
      {(tousGens.length > 6 || monEquipe.size > 0) && (
        <FilterBar
          q={q} onQ={setQ} placeholder="Rechercher un salarié…"
          chips={monEquipe.size > 0 ? [
            { k: 'tous', label: 'Tout le laboratoire', n: tousGens.length },
            { k: 'equipe', label: 'Mon équipe', n: tousGens.filter((p) => monEquipe.has(p.orgId)).length },
          ] : []}
          actif={portee} onChip={setPortee}
          total={tousGens.length} affiches={gens.length}
          onReset={() => { setQ(''); setPortee('tous'); }} />
      )}

      {!tousGens.length && rhChargement > 0 ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={6} /></div>
      ) : !gens.length ? (
        <EmptyState
          titre={tousGens.length === 0 ? 'Aucune présence pour cette semaine' : 'Aucun salarié ne correspond'}
          indice={tousGens.length === 0
            ? 'Importez un fichier de pointage, ou vérifiez que les badges sont bien rattachés aux salariés dans leur fiche.'
            : `${tousGens.length} salarié(s) ont des présences cette semaine — seule la recherche les masque.`}
        />
      ) : (
        <>
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', width: '100%', minWidth: 880 }}>
              <thead>
                <tr>
                  <th scope="col" style={{ ...th, textAlign: 'start', minWidth: 170, position: 'sticky', insetInlineStart: 0, background: 'var(--gray-25)' }}>Salarié</th>
                  {jours.map((d, i) => (
                    <th scope="col" key={d} style={th}>{JOURS[i]}<br /><span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>{fmtJour(d)}</span></th>
                  ))}
                  <th scope="col" style={th}>Retenu</th>
                </tr>
              </thead>
              <tbody>
                {gens.map((p) => {
                  const total = jours.reduce((n, d) => {
                    const c = caseDe(p.orgId, d);
                    return n + (c ? Number(c.heuresRetenues ?? c.heuresPointees ?? 0) : 0);
                  }, 0);
                  return (
                    <tr key={p.orgId}>
                      <th scope="row" style={{ ...td, textAlign: 'start', fontWeight: 600, position: 'sticky', insetInlineStart: 0, background: 'var(--surface-card)' }}>{p.nom}</th>
                      {jours.map((d) => {
                        const c = caseDe(p.orgId, d);
                        if (!c) return <td key={d} style={{ ...td, color: 'var(--text-faint)' }}>·</td>;
                        /* Corrigée par un humain : plus rouge — la décision est prise. */
                        const traitee = !!c.corrigePar && ['anomalie', 'absence_non_pointee'].includes(c.etat);
                        const e = traitee ? ETATS.conforme : (ETATS[c.etat] || ETATS.conforme);
                        const corrige2 = c.heuresRetenues != null && Math.abs(c.heuresRetenues - c.heuresPointees) > 0.01;
                        const choisie = masse && choisies.has(c.documentId);
                        return (
                          <td key={d}
                            style={{
                              ...td, background: e.fond,
                              cursor: masse && c.validee ? 'not-allowed' : 'pointer',
                              outline: choisie ? '2px solid var(--color-primary-active)' : undefined,
                              outlineOffset: choisie ? -2 : undefined,
                              opacity: masse && c.validee ? 0.45 : 1,
                            }}
                            onClick={() => {
                              if (!masse) { setCorrige(c); return; }
                              if (c.validee) return;
                              setChoisies((s) => {
                                const n = new Set(s);
                                if (n.has(c.documentId)) n.delete(c.documentId); else n.add(c.documentId);
                                return n;
                              });
                            }}
                            title={`${e.libelle}${c.anomalies?.length ? ` — ${c.anomalies.map((a) => LIB_ANOMALIE[a] || a).join(', ')}` : ''}${masse && c.validee ? ' — validée : rouvrez-la pour la corriger' : ''}`}>
                            {/* LA GARDE SE VOIT (RQ, 29/09/2026) — même quand la nuit
                                n'a laissé aucun passage. */}
                            {/* LENDEMAIN DE GARDE (RQ, 30/09/2026) : l'horaire attendu
                                est plus souple — l'après-midi reprend à 14 h, le matin
                                finit à 11 h. Le dire évite de lire un « retard » ou un
                                « départ anticipé » qui n'en sont pas. */}
                            {c.apresGarde && (
                              <div title="Post garde : horaires aménagés — poste d’après-midi à partir de 14 h, poste du matin jusqu’à 11 h. Le retard et les heures prévues sont calculés sur cet horaire."
                                style={{ display: 'inline-block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.02em',
                                  border: '1px solid var(--color-primary-active)', color: 'var(--color-primary-active)',
                                  background: 'var(--surface-card)', borderRadius: 'var(--radius-xs)', padding: '0 5px', marginBottom: 2, marginInlineEnd: 3 }}>
                                post garde
                              </div>
                            )}
                            {(c.postes || []).includes('GARDE') && (
                              <div style={{ display: 'inline-block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em',
                                background: 'var(--color-primary-active)', color: 'var(--text-on-primary)', borderRadius: 'var(--radius-xs)', padding: '0 5px', marginBottom: 2 }}>
                                GARDE
                              </div>
                            )}
                            <div style={{ fontWeight: 600, color: e.texte }}>
                              {h(c.heuresPointees)} {e.court && <span aria-hidden>{e.court}</span>}
                            </div>
                            <Passages j={c} />
                            {c.heuresPlanifiees > 0 && (
                              <div style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>prévu {h(c.heuresPlanifiees)}</div>
                            )}
                            {corrige2 && (
                              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>retenu {h(c.heuresRetenues)}</div>
                            )}
                            {traitee && !c.validee && <div style={{ fontSize: 9.5, color: 'var(--green-600)' }} title={`Corrigée par ${userName(settings, c.corrigePar)}${c.motifEcart ? ` — ${c.motifEcart}` : ''}`}>✓ corrigée</div>}
                            {c.validee && <div style={{ fontSize: 9.5, color: 'var(--green-600)' }}>validée</div>}
                          </td>
                        );
                      })}
                      <td style={{ ...td, fontWeight: 700 }}>{h(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-muted)' }}>
            {Object.entries(ETATS).filter(([k]) => k !== 'conforme').map(([k, v]) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ background: v.fond, color: v.texte, fontWeight: 700, borderRadius: 'var(--radius-xs)', padding: '1px 6px' }}>{v.court}</span>
                {v.libelle}
              </span>
            ))}
          </div>


          {/* Forcer malgré des anomalies relève du RQ (06/09/2026) — sans lui,
              `ConfirmButton` ne porte pas de champ pour l'authentifier : on
              bascule alors sur un panneau dédié, comme pour les autres
              dérogations. Qui n'a pas d'anomalie à forcer, ou qui est déjà
              RQ, garde le geste simple d'un clic. */}
          {!masse && nonValidees > 0 && (aTraiter === 0 || estRQ) && (
            <ConfirmButton
              label={`Valider la semaine (${nonValidees} journée(s))`} variant="primary" icon="check"
              // La validation porte sur la SEMAINE ENTIÈRE, pas sur les lignes
              // affichées. Filtrer sur une personne puis valider toucherait tout
              // le monde : le dire est moins coûteux que de le découvrir après.
              question={`${aTraiter > 0
                ? `${aTraiter} journée(s) restent à traiter. Valider quand même ?`
                : `Valider ${nonValidees} journée(s) ? Elles ne seront plus recalculées par un import.`}`
                + (qq ? ' Attention : la validation couvre TOUS les salariés de la semaine, pas seulement ceux affichés.' : '')}
              confirmer="Valider"
              onConfirm={() => validerPresences(jours[0], jours[6], aTraiter > 0)}
            />
          )}
          {!masse && nonValidees > 0 && aTraiter > 0 && !estRQ && !forcageOuvert && (
            <Bouton variante="primaire" taille="sm" onClick={() => setForcageOuvert(true)}>
              <I n="check" s={13} />Valider la semaine ({nonValidees} journée(s))
            </Bouton>
          )}
          {!masse && nonValidees > 0 && aTraiter > 0 && !estRQ && forcageOuvert && (
            <div style={{ ...card, padding: 12, display: 'grid', gap: 8, borderInlineStart: '3px solid var(--amber-500)' }}>
              <strong style={{ fontSize: 13 }}>{aTraiter} journée(s) restent à traiter. Valider quand même ?</strong>
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
                Forcer la validation malgré des anomalies relève du RQ.
                {qq && ' Attention : la validation couvre TOUS les salariés de la semaine, pas seulement ceux affichés.'}
              </p>
              <ChampsResponsable {...authForcage} libelle="cette validation" />
              <PiedActions taille="sm" annuler={{ onClick: () => { setForcageOuvert(false); authForcage.reinitialiser(); } }} principale={{ libelle: "Valider", disabled: !authForcage.pret, onClick: async () => {
                    const ok = await validerPresences(jours[0], jours[6], true, authForcage.payload);
                    if (ok) { setForcageOuvert(false); authForcage.reinitialiser(); }
                  } }} />
            </div>
          )}
        </>
      )}

      {importer && <DialogueImport onFermer={() => setImporter(false)} onImporter={importerPointages} />}
      {masseDialogue && (
        <DialogueMasse
          n={choisies.size}
          onFermer={() => setMasseDialogue(false)}
          onCorriger={async (mode, motif, heures) => {
            const ok = await corrigerJourneesEnMasse([...choisies], mode, motif, heures);
            if (ok) { setMasseDialogue(false); setChoisies(new Set()); setMasse(false); }
          }}
        />
      )}
      {corrige && (
        <DialogueCorrection
          j={corrige} onFermer={() => setCorrige(null)}
          onCorriger={async (heures, motif, reel) => { const ok = await corrigerJournee(corrige.documentId, heures, motif, reel); if (ok) setCorrige(null); }}
          onRouvrir={async () => { const ok = await rouvrirJournee(corrige.documentId); if (ok) setCorrige(null); }}
          peutRouvrir={!!rhAcces?.acces}
        />
      )}
      </>)}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Pointage BRUT — lecture seule, et strictement (lot GED-15 b, 07/08/2026)
 *
 * `GET ged/rh/pointages` était autorisée depuis sa livraison et n'était servie
 * à AUCUN écran : la preuve existait, et personne ne pouvait la regarder. Or
 * c'est elle qui tranche. La journée affichée dans la grille ci-dessus est le
 * RETENU — reconstitué, corrigeable, validable. Le brut est ce à partir de quoi
 * il a été reconstitué, et la règle R2 en fait la référence opposable de la
 * paie. Un gestionnaire à qui l'on conteste un écart doit pouvoir y remonter.
 *
 * ⚠️ AUCUNE écriture, et ce n'est pas une omission — c'est l'objet même de
 * l'écran. Le brut est immuable par conception : le corriger lui retirerait ce
 * qui fait sa valeur, et retirerait à la paie ce sur quoi elle s'appuie. Il n'y
 * a donc ici ni champ, ni bouton d'action, ni appel de magasin autre que la
 * lecture — et il n'existe pas de route pour le faire, même si on le voulait.
 *
 * C'est aussi pourquoi la journée retenue et l'ÉCART sont affichés À CÔTÉ de
 * chaque groupe : si la lecture du brut donne envie de corriger une ligne, la
 * réponse n'est pas d'ouvrir l'écriture ici, c'est d'aller corriger la journée
 * — avec son motif et son auteur, dans la grille du dessus. Montrer les deux
 * côte à côte est ce qui rend ce geste-là évident et l'autre inutile.
 * ══════════════════════════════════════════════════════════════════════════ */

const SENS: Record<string, string> = { entree: 'Entrée', sortie: 'Sortie', E: 'Entrée', S: 'Sortie' };
const hhmm = (ts: string) => {
  // On lit l'horodatage TEL QU'IL EST STOCKÉ, sans passer par `Date` : le brut
  // ne se recale pas sur le fuseau du navigateur. Un pointage de 06:58 affiché
  // 07:58 parce que le poste est mal réglé ferait mentir la preuve.
  const m = /T(\d{2}:\d{2})/.exec(ts);
  return m ? m[1] : ts;
};

function PointageBrut({ jours, pointages, journees, chargement, filtre, portee, equipe }: {
  jours: string[]; pointages: RhPointage[]; journees: RhJournee[];
  chargement: boolean; filtre: string; portee: string; equipe: Set<string>;
}) {
  const settings = useGed((s) => s.settings);
  // Le brut suit les MÊMES filtres que la grille : deux périmètres différents
  // sur un même écran donneraient un écart apparent qui n'existe pas.
  const retenus = React.useMemo(() => {
    const m = new Map<string, RhJournee>();
    for (const j of journees) if (j.personnel) m.set(`${j.personnel.orgId}|${j.date}`, j);
    return m;
  }, [journees]);

  const groupes = React.useMemo(() => {
    const g = new Map<string, { nom: string; orgId: string; date: string; points: RhPointage[] }>();
    for (const p of pointages) {
      const orgId = p.personnel?.orgId ?? `?${p.badgeId}`;
      const date = jourIso(p.ts || '');
      const cle = `${orgId}|${date}`;
      if (!g.has(cle)) g.set(cle, { nom: nomDe(p.personnel) || `Badge ${p.badgeId} — rattaché à personne`, orgId, date, points: [] });
      g.get(cle)!.points.push(p);
    }
    return [...g.values()]
      .filter((x) => portee !== 'equipe' || equipe.has(x.orgId))
      .filter((x) => !filtre || x.nom.toLowerCase().includes(filtre))
      .sort((a, b) => a.nom.localeCompare(b.nom) || a.date.localeCompare(b.date));
  }, [pointages, filtre, portee, equipe]);

  return (
    <div style={{ ...card, padding: 0, marginTop: 14, borderInlineStart: '3px solid var(--color-primary-active)' }}>
      <div style={{ padding: '16px 20px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <I n="lock" s={15} />
          <strong style={{ fontSize: 13.5 }}>Pointage brut — lecture seule</strong>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 0', maxWidth: 820 }}>
          Les passages de badge tels qu’ils ont été enregistrés, du {fmtJour(jours[0])} au {fmtJour(jours[6])} :
          heure exacte, sens, source et lot d’import. <strong>Ils ne se modifient pas</strong> — c’est
          leur immuabilité qui en fait la référence opposable de la paie. La journée
          retenue et l’écart sont rappelés à droite de chaque groupe : si une ligne paraît
          fausse, c’est la <em>journée</em> qui se corrige, dans la grille ci-dessus, avec son motif
          et son auteur.
        </p>
      </div>

      {chargement && !groupes.length ? (
        <Skeleton rows={5} />
      ) : !groupes.length ? (
        <div style={{ padding: '0 20px 18px' }}>
          <EmptyState
            titre="Aucun pointage brut sur cette semaine"
            indice="Aucun passage de badge n’a été importé pour cette période. Les journées affichées au-dessus, s’il y en a, viennent alors du planning seul."
          />
        </div>
      ) : (
        <>
          <Tableau
            id="rh-pointage-brut"
            densite="compacte"
            defilementHorizontal
            lignes={groupes}
            cleLigne={(g) => `${g.orgId}|${g.date}`}
            colonnes={[
              { cle: 'nom', libelle: 'Salarié', valeur: (g) => g.nom, epinglee: true, obligatoire: true, style: { fontWeight: 600, minWidth: 170 }, rendu: (g) => g.nom },
              { cle: 'date', libelle: 'Jour', valeur: (g) => g.date, rendu: (g) => fmtJour(g.date) },
              {
                cle: 'passages', libelle: 'Passages enregistrés', valeur: (g) => g.points.length, style: { whiteSpace: 'normal' },
                rendu: (g) => {
                  const impair = g.points.length % 2 !== 0;
                  return (
                    <>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {g.points.map((p) => (
                          <li key={p.documentId}
                            title={`Source : ${p.source || 'non précisée'} · lot d’import ${p.lotImport || '—'} · badge ${p.badgeId}`}
                            style={{ background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', padding: '3px 8px', fontSize: 12.5 }}>
                            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{hhmm(p.ts)}</strong>{' '}
                            <span style={{ color: 'var(--text-muted)' }}>{SENS[p.sens] || p.sens || '?'}</span>
                          </li>
                        ))}
                      </ul>
                      {/* Un nombre impair de passages, c'est une entrée ou une sortie
                          qui manque. Le dire ICI, sur le brut, évite d'aller chercher
                          la cause d'une anomalie dont la trace est sous les yeux. */}
                      {impair && (
                        <div style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 4 }}>
                          {g.points.length} passage(s) — un pointage manque (entrée ou sortie non badgée).
                        </div>
                      )}
                      <div style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 4 }}>
                        source {g.points[0].source || 'non précisée'} · lot {g.points[0].lotImport || '—'}
                      </div>
                    </>
                  );
                },
              },
              {
                cle: 'retenue', libelle: 'Journée retenue', style: { whiteSpace: 'normal', minWidth: 190 },
                valeur: (g) => retenus.get(`${g.orgId}|${g.date}`)?.heuresRetenues ?? retenus.get(`${g.orgId}|${g.date}`)?.heuresPointees ?? null,
                rendu: (g) => {
                  const j = retenus.get(`${g.orgId}|${g.date}`);
                  const e = j ? (ETATS[j.etat] || ETATS.conforme) : null;
                  if (!j) {
                    return (
                      <span style={{ color: 'var(--red-600)', fontSize: 12.5 }}>
                        Aucune journée reconstituée — ces heures ne sont comptées pour personne.
                      </span>
                    );
                  }
                  return (
                    <>
                      <div style={{ fontWeight: 600 }}>
                        pointé {h(j.heuresPointees)}
                        {j.heuresRetenues != null && Math.abs(j.heuresRetenues - j.heuresPointees) > 0.01 && (
                          <> · <span style={{ color: 'var(--color-primary-active)' }}>retenu {h(j.heuresRetenues)}</span></>
                        )}
                      </div>
                      <div style={{ fontSize: 12.5, color: e!.texte }}>
                        {e!.libelle}
                        {j.heuresPlanifiees > 0 && <> · prévu {h(j.heuresPlanifiees)}</>}
                        {Math.abs(j.ecart) > 0.01 && <> · écart {j.ecart > 0 ? '+' : ''}{heuresLisibles(j.ecart)}</>}
                      </div>
                      {/* LE DÉTAIL DES VACATIONS (25/08/2026) : une ligne par vacation,
                          l'anomalie nommée à côté de celle qui la porte. */}
                      {!!j.segments?.length && (
                        <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'grid', gap: 2 }}>
                          {j.segments.map((sg, i) => (
                            <li key={i} style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: sg.anomalie ? 'var(--red-600)' : 'var(--text-muted)' }}>
                              {sg.debut ? hhmm(sg.debut) : '?'} → {sg.fin ? hhmm(sg.fin) : '?'}
                              {sg.heures > 0 && <> · {heuresLisibles(sg.heures)}</>}
                              {sg.anomalie && <> · {LIB_ANOMALIE[sg.anomalie] || sg.anomalie}</>}
                            </li>
                          ))}
                        </ul>
                      )}
                      {/* LE RETARD, seulement s'il y en a un : `null` veut dire « non mesurable », pas « à l'heure ». */}
                      {j.retardMin != null && j.retardMin > 0 && (
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', marginTop: 2 }}
                          title="Minutes au-delà de la tolérance de ponctualité, mesurées sur la première prise de poste planifiée du jour. La tolérance se règle dans le paramétrage RH.">
                          retard : {j.retardMin} min
                        </div>
                      )}
                      {j.motifEcart && (
                        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                          motif : {j.motifEcart}{j.corrigePar ? ` (${userName(settings, j.corrigePar)})` : ''}
                        </div>
                      )}
                      {j.validee && <div style={{ fontSize: 12.5, color: 'var(--green-600)' }}>validée</div>}
                    </>
                  );
                },
              },
            ]}
          />
          <p style={{ margin: 0, padding: '8px 20px 16px', fontSize: 12.5, color: 'var(--text-muted)' }}>
            {groupes.length} journée(s) de pointage · {pointages.length} passage(s) de badge sur la semaine.
            Aucun de ces éléments n’est modifiable depuis cet écran.
          </p>
        </>
      )}
    </div>
  );
}

function RapportImport({ onFermer }: { onFermer: () => void }) {
  const r = useGed((s) => s.rapportImport)!;
  const divergentes = r.validesDivergentes || [];
  /* Une divergence sur une journée validée est un PROBLÈME au même titre qu'un
     badge inconnu : dans les deux cas, des heures réelles risquent de ne
     compter pour personne. */
  const probleme = r.rejetsTotal > 0 || r.badgesInconnus.length > 0 || divergentes.length > 0
    || (r.controle || []).some((c) => c.gravite !== 'info');
  return (
    <div style={{ ...card, padding: 20, marginTop: 14, borderInlineStart: `3px solid ${probleme ? 'var(--amber-500)' : 'var(--green-600)'}` }}>
      <div style={{ display: 'flex', alignItems: 'start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: 13.5 }}>Import {r.lot}</strong>
          <p style={{ fontSize: 12.5, margin: '4px 0' }}>
            Du {fmtJour(r.periode.du)} au {fmtJour(r.periode.au)} ·{' '}
            {r.crees} pointage(s) retenu(s) · {r.doublons} doublon(s) ignoré(s) ·{' '}
            {r.journeesCalculees} journée(s) recalculée(s)
            {r.journeesValideesIgnorees > 0 && <> · {r.journeesValideesIgnorees} déjà validée(s), laissée(s) intacte(s)</>}
          </p>

          {r.badgesInconnus.length > 0 && (
            <div style={{ background: 'var(--red-50)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', marginTop: 8 }}>
              <strong style={{ fontSize: 12.5, color: 'var(--red-600)' }}>
                Badges rattachés à personne : {r.badgesInconnus.join(', ')}
              </strong>
              <p style={{ fontSize: 12.5, margin: '3px 0 0' }}>
                Ces heures ne sont comptées pour personne. Renseignez le badge sur la fiche du
                salarié concerné, puis relancez l’import — les doublons seront ignorés.
              </p>
            </div>
          )}

          {/* ⚠️ LES JOURNÉES VALIDÉES QUE CET IMPORT AURAIT CHANGÉES (26/08/2026).
              Elles ne sont PAS touchées — une validation ne s'efface pas toute
              seule — mais se taire les rendrait fausses en silence. Le cas type :
              la garde du 31 déborde sur le 1er du mois suivant ; quand le fichier
              de ce mois arrive, le précédent est validé, et douze heures de garde
              pourraient manquer sans que personne ne l'apprenne. */}
          {divergentes.length > 0 && (
            <div style={{ background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', marginTop: 8 }}>
              <strong style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
                {divergentes.length} journée(s) déjà validée(s) auraient changé — laissée(s) intacte(s)
              </strong>
              <p style={{ fontSize: 12.5, margin: '3px 0 6px' }}>
                Ces pointages concernent des journées que vous avez déjà validées. Elles n’ont pas été
                réécrites. Rouvrez celles qui doivent tenir compte de cet import — une à une, pour que
                la reprise reste un geste explicite.
              </p>
              <ul style={{ fontSize: 12.5, margin: 0, paddingInlineStart: 18 }}>
                {divergentes.slice(0, 20).map((x) => (
                  <li key={`${x.orgId}-${x.date}`}>
                    <strong>{x.nom}</strong>, {fmtJour(x.date)} : {x.avant} → deviendrait {x.apres}
                  </li>
                ))}
              </ul>
              {divergentes.length > 20 && (
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  … et {divergentes.length - 20} autre(s).
                </p>
              )}
            </div>
          )}

          {/* LES INCOHÉRENCES de la période importée (29/09/2026). */}
          {(r.controle?.length ?? 0) > 0 && (
            <details open={r.controle!.some((c) => c.gravite === 'bloquant')} style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 12.5, cursor: 'pointer', fontWeight: 600 }}>
                {r.controle!.length} incohérence(s) entre le fichier, le planning, les absences et les fiches
              </summary>
              <div style={{ marginTop: 6 }}><ListeConstats constats={r.controle!} /></div>
            </details>
          )}

          {r.rejetsTotal > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 12.5, cursor: 'pointer', color: 'var(--amber-600)', fontWeight: 600 }}>
                {r.rejetsTotal} ligne(s) rejetée(s) — voir le détail
              </summary>
              <ul style={{ fontSize: 12.5, margin: '6px 0 0', paddingInlineStart: 18 }}>
                {r.rejets.map((x) => (
                  <li key={x.ligne}><strong>ligne {x.ligne}</strong> : {x.raison} <code style={{ color: 'var(--text-faint)' }}>{x.contenu.slice(0, 60)}</code></li>
                ))}
              </ul>
              {r.rejetsTotal > r.rejets.length && (
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>… et {r.rejetsTotal - r.rejets.length} autre(s).</p>
              )}
            </details>
          )}
        </div>
        <Bouton taille="sm" onClick={onFermer} aria-label="Fermer le rapport d’import">✕</Bouton>
      </div>
    </div>
  );
}

/** Correction de MASSE — un mode, un motif, appliqués à toute la sélection.
 *  Le moteur rejoue les gardes unitaires par journée et NOMME les écartées. */
function DialogueMasse({ n, onFermer, onCorriger }: {
  n: number; onFermer: () => void;
  onCorriger: (mode: 'planifiees' | 'pointees' | 'heures', motif: string, heures?: number) => Promise<void>;
}) {
  const [mode, setMode] = React.useState<'planifiees' | 'pointees' | 'heures'>('planifiees');
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(mode);
  const [heures, setHeures] = React.useState('8');
  const [motif, setMotif] = React.useState('');
  const [envoi, setEnvoi] = React.useState(false);
  const saisi = parseFloat(heures.replace(',', '.'));
  const heuresOk = mode !== 'heures' || (Number.isFinite(saisi) && saisi >= 0 && saisi <= 24);
  // Même règle qu'à la correction unitaire : jamais de bouton mort sans phrase.
  const refus = !heuresOk
    ? 'Saisissez les heures retenues (un nombre entre 0 et 24 — la virgule est acceptée).'
    : motif.trim().length < 5
      ? `Une correction de masse se motive : 5 caractères au minimum${motif.trim() ? ` (${motif.trim().length} pour l’instant)` : ''}.`
      : '';
  const pret = !refus;
  const MODES: { k: 'planifiees' | 'pointees' | 'heures'; l: string; aide: string }[] = [
    { k: 'planifiees', l: 'Retenir les heures planifiées', aide: 'Le cas badgeuse en panne : chacun est réputé avoir fait son planning. Les journées sans heures planifiées sont écartées et nommées.' },
    { k: 'pointees', l: 'Revenir aux heures pointées', aide: 'Efface une correction antérieure : le retenu redevient le pointé.' },
    { k: 'heures', l: 'Retenir une valeur fixe', aide: 'La même durée pour toutes les journées sélectionnées.' },
  ];
  return (
    <Modale titre={`Corriger ${n} journée(s)`}
      sousTitre="Un seul motif, tracé sur chaque journée et au journal. Les pointages d’origine sont conservés."
      onFermer={onFermer}>
      <div style={{ display: 'grid', gap: 8 }}>
        {MODES.map((m) => (
          <label key={m.k} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 12.5 }}>
            <input type="radio" name="mode-masse" checked={mode === m.k} onChange={() => setMode(m.k)} style={{ marginTop: 2 }} />
            <span><strong>{m.l}</strong><br /><span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>{m.aide}</span></span>
          </label>
        ))}
      </div>
      {mode === 'heures' && (
        <label style={{ ...lbl, marginTop: 10 }}>Heures retenues (0 à 24)
          <input type="text" inputMode="decimal" value={heures}
            onChange={(e) => setHeures(e.target.value)} style={{ ...inputStyle, width: 120 }} />
        </label>
      )}
      <label style={{ ...lbl, marginTop: 10 }}>Motif (obligatoire, commun à la sélection)
        <textarea rows={2} value={motif} onChange={(e) => setMotif(e.target.value)}
          placeholder="Badgeuse en panne le matin du 12/08 — heures planifiées retenues."
          style={{ ...inputStyle, width: '100%', resize: 'vertical' }} />
      </label>
      {refus && <FieldError>{refus}</FieldError>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'end', marginTop: 16 }}>
        <Bouton onClick={onFermer} disabled={envoi}>Annuler</Bouton>
        <Bouton variante="primaire" disabled={!pret || envoi} onClick={async () => {
            setEnvoi(true);
            try { await onCorriger(mode, motif.trim(), mode === 'heures' ? saisi : undefined); }
            finally { setEnvoi(false); }
          }}>
          {envoi ? 'Correction…' : `Corriger ${n} journée(s)`}
        </Bouton>
      </div>
    </Modale>
  );
}

function DialogueImport({ onFermer, onImporter }: {
  onFermer: () => void;
  onImporter: (contenu: string, mappage?: Record<string, unknown>, classeurB64?: string) => Promise<boolean>;
}) {
  const [contenu, setContenu] = React.useState('');
  /* Un classeur voyage en BASE64 : c'est une archive ZIP, pas du texte.
     `FileReader.readAsText` sur un binaire rendrait des remplacements Unicode
     et l'archive serait irrécupérable côté serveur — un import « vide » sans
     rien pour comprendre pourquoi. */
  const [classeurB64, setClasseurB64] = React.useState('');
  const [nomFichier, setNomFichier] = React.useState('');
  const [sep, setSep] = React.useState(';');
  const [formatDate, setFormatDate] = React.useState<'jma' | 'iso'>('jma');
  const [colBadge, setColBadge] = React.useState('badge');
  const [colDate, setColDate] = React.useState('date');
  const [colHeure, setColHeure] = React.useState('heure');
  const [colSens, setColSens] = React.useState('sens');
  const [envoi, setEnvoi] = React.useState(false);
  useEscape(true, onFermer);

  const lire = (f: File | null) => {
    if (!f) return;
    setNomFichier(f.name);
    const r = new FileReader();
    /* Le format se décide sur l'EXTENSION ici (le navigateur n'ouvre pas le
       fichier) ; le serveur, lui, tranchera sur le CONTENU. `.xls` en fait
       partie depuis le 10/09/2026 : c'est ce que la badgeuse exporte par
       défaut, et le lire en TEXTE renverrait des octets illisibles au lieu du
       classeur. */
    if (/\.(xls|xlsx|xlsm|ods)$/i.test(f.name)) {
      r.onload = () => {
        const d = String(r.result || '');
        setClasseurB64(d.slice(d.indexOf(',') + 1));
        setContenu('');
      };
      r.readAsDataURL(f);
      return;
    }
    r.onload = () => { setContenu(String(r.result || '')); setClasseurB64(''); };
    r.readAsText(f);
  };

  const apercu = contenu.split(/\r?\n/).slice(0, 3);

  return (
    <Modale titre="Importer un fichier de pointage" onFermer={onFermer}>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        Export de la badgeuse : <strong>.xls</strong> (le format natif de ZKTime), .xlsx, .ods, CSV ou texte.
        Les colonnes sont configurables : le jour où la badgeuse exposera une API, seule cette
        correspondance changera — les pointages déjà enregistrés, eux, ne bougeront pas.
      </p>

      <label style={{ ...lbl, marginTop: 10 }}>Fichier
        <input type="file" accept=".csv,.txt,.xls,.xlsx,.xlsm,.ods,text/*" style={{ ...inputStyle, width: '100%', padding: 6 }}
          onChange={(e) => lire(e.target.files?.[0] || null)} />
      </label>

      {apercu.length > 0 && contenu && (
        <pre style={{ fontSize: 10.5, background: 'var(--gray-25)', padding: 8, borderRadius: 'var(--radius-sm)', overflowX: 'auto', marginTop: 8 }}>
          {apercu.join('\n')}{contenu.split(/\r?\n/).length > 3 ? '\n…' : ''}
        </pre>
      )}

      {/* Un classeur n'a PAS d'aperçu ici : son contenu est une archive que
          seul le serveur ouvre. Nommer le fichier retenu vaut mieux que la
          zone vide d'un aperçu impossible — au moins on sait ce qui partira. */}
      {!!classeurB64 && (
        <p style={{ fontSize: 12.5, marginTop: 8, padding: '8px 10px', background: 'var(--cyan-50)', borderRadius: 'var(--radius-sm)', color: 'var(--text-body)' }}>
          Classeur retenu : <strong>{nomFichier}</strong>. Sa première feuille sera lue ;
          la correspondance des colonnes ci-dessous s’applique à son en-tête.
          <br />
          <span style={{ color: 'var(--text-muted)' }}>
            Le séparateur ne s’applique pas à un classeur — ses colonnes sont déjà séparées.
          </span>
        </p>
      )}

      <details style={{ marginTop: 10 }}>
        <summary style={{ fontSize: 12.5, cursor: 'pointer', fontWeight: 600 }}>
          Correspondance des colonnes <Help aide="À régler une fois, d’après l’en-tête de votre fichier. Si l’import rejette tout, c’est presque toujours ici que ça se joue.">aide</Help>
        </summary>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
          <label style={lbl}>Colonne badge<input style={inputStyle} value={colBadge} onChange={(e) => setColBadge(e.target.value)} /></label>
          <label style={lbl}>Colonne sens<input style={inputStyle} value={colSens} onChange={(e) => setColSens(e.target.value)} /></label>
          <label style={lbl}>Colonne date<input style={inputStyle} value={colDate} onChange={(e) => setColDate(e.target.value)} /></label>
          <label style={lbl}>Colonne heure<input style={inputStyle} value={colHeure} onChange={(e) => setColHeure(e.target.value)} /></label>
          <label style={lbl}>Séparateur<input style={inputStyle} value={sep} onChange={(e) => setSep(e.target.value)} maxLength={1} /></label>
          <label style={lbl}>Format de date
            <select style={inputStyle} value={formatDate} onChange={(e) => setFormatDate(e.target.value as 'jma' | 'iso')}>
              <option value="jma">JJ/MM/AAAA</option>
              <option value="iso">AAAA-MM-JJ</option>
            </select>
          </label>
        </div>
      </details>

      <PiedActions taille="sm" style={{ marginTop: 16 }} annuler={{ onClick: onFermer, disabled: envoi }} principale={{ libelle: 'Importer', libelleChargement: 'Import en cours…', chargement: envoi, disabled: !(contenu.trim() || classeurB64) || envoi, onClick: async () => {
            setEnvoi(true);
            const ok = await onImporter(contenu, {
              badgeId: colBadge, date: colDate, heure: colHeure, sens: colSens,
              separateur: sep, formatDate,
            }, classeurB64 || undefined);
            setEnvoi(false);
            if (ok) onFermer();
          } }} />
    </Modale>
  );
}

/**
 * LES PASSAGES DE LA BADGEUSE pour cette case (RQ, 29/09/2026 : « quand tu
 * indiques passage refusé par la badgeuse, comment je peux le vérifier ? Je
 * voudrais pouvoir le corriger depuis la case »).
 *
 * Chaque passage montre ce que ZKTime a écrit — sa note (« OK »,
 * « Invalide »), le sens choisi sur la machine, et le sens qu'elle a recalculé
 * (vide quand elle refuse). On le CONFIRME (la réserve est levée) ou on
 * l'ÉCARTE (il ne compte plus) ; le brut, lui, reste tel quel.
 */
function PassagesBadgeuse({ j, veille: veilleFournie }: { j: RhJournee; veille?: RhJournee }) {
  const rhPointages = useGed((s) => s.rhPointages);
  const loadPointages = useGed((s) => s.loadPointages);
  const verifier = useGed((s) => s.verifierPointage);
  const postes = useGed((s) => s.rhPostes);
  /* La veille : une sortie de garde du matin appartient à la journée d'hier. */
  const veilleStore = useGed((s) => s.rhJournees.find((x) => x.personnel?.orgId === j.personnel?.orgId
    && x.date === isoJour(new Date(new Date(`${j.date}T12:00:00Z`).getTime() - 86400000))));
  const veille = veilleFournie || veilleStore;
  const [ouvert, setOuvert] = React.useState<{ id: string; statut: 'confirme' | 'ecarte' | 'aucune' | 'sens'; sens?: 'entree' | 'sortie' } | null>(null);
  const [motif, setMotif] = React.useState('');
  const [envoi, setEnvoi] = React.useState(false);
  /* La semaine DE LA JOURNÉE, pas celle affichée : ouverte depuis la fiche
     d'une personne (30/09/2026), la journée peut être dans une autre semaine. */
  React.useEffect(() => { void loadPointages(lundiDe(new Date(`${j.date}T12:00:00`))); }, [loadPointages, j.date]);

  /* Ceux du jour, plus ceux que les segments de la journée utilisent (la
     sortie de garde du lendemain matin). */
  const dansSegments = new Set((j.segments || []).flatMap((sg) => [sg.debut, sg.fin]).filter(Boolean) as string[]);
  const siens = rhPointages
    .filter((p) => p.personnel?.orgId === j.personnel?.orgId && (jourIso(p.ts) === j.date || dansSegments.has(p.ts)))
    .sort((a, b) => a.ts.localeCompare(b.ts));
  if (!siens.length) return null;
  const sens = (x: string) => (x === 'entree' ? 'Entrée' : x === 'sortie' ? 'Sortie' : x);
  /* ── L'AVIS DE L'OUTIL (RQ, 30/09/2026 : « ZKT nous dit juste entrée ou
     sortie, et on interprète dans l'appli ») ─────────────────────────────
     Le rôle que l'outil donne à chaque passage, lu sur les vacations de la
     journée : entrée ou sortie de QUEL poste, à l'heure ou non, et le sens
     qu'il aurait fallu choisir. Plus rien de l'avis de ZKTime. */
  const libPoste = (c?: string) => (!c ? '' : c === 'GARDE' ? 'garde' : postes.find((x) => x.code === c)?.libelle || c);
  const ecartMin = (a: string, b?: string) => (b ? Math.round((Date.parse(a) - Date.parse(b)) / 60000) : 0);
  const avisDe = (ts: string, choisi: string, precedent?: string): { texte: string; alerte?: boolean; sensAttendu?: 'entree' | 'sortie' } => {
    const segs = [...(j.segments || []), ...(veille?.segments || [])] as { debut: string | null; fin: string | null; poste?: string; attenduDebut?: string; attenduFin?: string }[];
    const e = segs.find((sg) => sg.debut === ts);
    if (e) {
      const m = ecartMin(ts, e.attenduDebut);
      const quand = !e.attenduDebut ? '' : m > 5 ? ` · ${m} min après l’heure prévue` : m < -30 ? ` · ${-m} min en avance` : ' · à l’heure';
      return { texte: `entrée · ${libPoste(e.poste)}${quand}`, sensAttendu: 'entree', alerte: choisi !== 'entree' };
    }
    const f = segs.find((sg) => sg.fin === ts);
    if (f) {
      const m = ecartMin(ts, f.attenduFin);
      const quand = !f.attenduFin ? '' : m < -5 ? ` · ${-m} min avant la fin prévue` : m > 15 ? ` · ${m} min après la fin prévue` : ' · à l’heure';
      return { texte: `sortie · ${libPoste(f.poste)}${quand}`, sensAttendu: 'sortie', alerte: choisi !== 'sortie' };
    }
    if (precedent && Math.abs(ecartMin(ts, precedent)) <= 2) return { texte: 'badge repassé (moins de 2 min) — non retenu' };
    return { texte: 'hors planning — non rattaché à une vacation', alerte: true };
  };

  return (
    <div style={{ marginTop: 12 }}>
      <strong style={{ fontSize: 12.5 }}>Passages lus par la badgeuse</strong>
      {/* L10 (11/10/2026) : registre SIMPLE sur le socle. L'avis de l'outil se calcule
          sur le passage PRÉCÉDENT dans l'ordre chronologique — il est posé sur chaque
          ligne AVANT le tri, pour qu'un tri ne change pas l'interprétation. */}
      <Tableau
        id="rh-passages-journee"
        niveau="simple"
        densite="compacte"
        lignes={siens.map((p, i) => ({ ...p, avis: avisDe(p.ts, (p.verification?.sens || p.sens) as string, siens[i - 1]?.ts) }))}
        cleLigne={(p) => p.documentId}
        ligneProps={(p) => ({ style: { opacity: p.verification?.statut === 'ecarte' ? 0.55 : 1 } })}
        colonnes={[
          {
            cle: 'ts', libelle: 'Heure', valeur: (p) => p.ts, style: { fontVariantNumeric: 'tabular-nums' },
            rendu: (p) => (
              <span style={{ textDecoration: p.verification?.statut === 'ecarte' ? 'line-through' : undefined }}>
                {jourIso(p.ts) !== j.date && <span style={{ color: 'var(--text-muted)' }}>{fmtJour(jourIso(p.ts))} </span>}
                {p.ts.slice(11, 16)}
              </span>
            ),
          },
          {
            cle: 'sens', libelle: 'Sens choisi', type: 'liste', valeur: (p) => p.verification?.sens || p.sens, libelleValeur: (v) => sens(v), style: { whiteSpace: 'nowrap' },
            rendu: (p) => (p.verification?.sens && p.verification.sens !== p.sens
              ? <><s style={{ color: 'var(--text-muted)' }}>{sens(p.sens)}</s> → <strong>{sens(p.verification.sens)}</strong></>
              : sens(p.sens)),
          },
          {
            cle: 'avis', libelle: 'Interprétation par l’outil', valeur: (p) => p.avis.texte,
            rendu: (p) => (
              <span style={{ color: p.avis.alerte ? 'var(--amber-600)' : 'var(--text-body)' }}>
                {p.avis.texte}
                {p.avis.sensAttendu && p.avis.alerte && (
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 600 }}>
                    « {sens((p.verification?.sens || p.sens) as string)} » choisi, « {sens(p.avis.sensAttendu)} » attendu — à inverser ?
                  </span>
                )}
              </span>
            ),
          },
          {
            cle: 'verification', libelle: 'Vérification', type: 'liste',
            valeur: (p) => (p.verification ? (p.verification.statut === 'confirme' ? (p.verification.sens && p.verification.sens !== p.sens ? 'sens corrigé' : 'confirmé') : 'écarté') : ''),
            rendu: (p) => (p.verification
              ? <span title={`${p.verification.motif} — ${p.verification.par}, ${fmtDate(p.verification.le)}`}>{p.verification.statut === 'confirme' ? (p.verification.sens && p.verification.sens !== p.sens ? '⇄ sens corrigé' : '✓ confirmé') : '✕ écarté'}</span>
              : '—'),
          },
        ]}
        actions={(p) => (
          <>
            {!j.validee && (
              <Bouton taille="sm" type="button" title="Entrée/Sortie mal choisi sur la machine : inverser le sens (le passage d’origine est conservé)" onClick={() => {
                  const actuel = (p.verification?.sens || p.sens) as 'entree' | 'sortie';
                  const inverse = actuel === 'entree' ? 'sortie' : 'entree';
                  setOuvert({ id: p.documentId, statut: 'sens', sens: inverse });
                  setMotif(`Sens mal choisi sur la badgeuse : ${sens(actuel)} → ${sens(inverse)}. `);
                }}>Inverser</Bouton>
            )}
            {p.verification ? (
              <Bouton taille="sm" type="button" onClick={() => { setOuvert({ id: p.documentId, statut: 'aucune' }); setMotif(''); }}>annuler</Bouton>
            ) : !j.validee && (
              <>
                {p.douteux && (
                  <Bouton taille="sm" type="button" onClick={() => { setOuvert({ id: p.documentId, statut: 'confirme' }); setMotif('Passage vérifié : il a bien eu lieu. '); }}>Confirmer</Bouton>
                )}
                <Bouton taille="sm" type="button" title="Ne pas tenir compte de ce passage dans le calcul (essai, badge prêté, double lecture). Il n’est PAS supprimé : il reste visible, barré, et la décision est tracée avec son motif. Réversible." onClick={() => { setOuvert({ id: p.documentId, statut: 'ecarte' }); setMotif(''); }}>Écarter</Bouton>
              </>
            )}
          </>
        )}
      />
      {ouvert && (
        <div style={{ marginTop: 8, padding: 10, background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', display: 'grid', gap: 6 }}>
          <label style={lbl}>
            {ouvert.statut === 'confirme' ? 'Confirmer ce passage' : ouvert.statut === 'ecarte' ? 'Écarter ce passage (essai, badge prêté, double lecture…)'
              : ouvert.statut === 'sens' ? `Corriger le sens en « ${sens(ouvert.sens || '')} »` : 'Retirer la vérification'}
            {' '}— motif <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(10 caractères au moins, il part au journal)</span>
            <textarea rows={2} style={{ ...inputStyle, width: '100%' }} value={motif} onChange={(e) => setMotif(e.target.value)} />
          </label>
          <PiedActions taille="sm" annuler={{ onClick: () => setOuvert(null), disabled: envoi }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: envoi, disabled: motif.trim().length < 10 || envoi, onClick: async () => {
                setEnvoi(true);
                const ok = await verifier(ouvert.id, ouvert.statut, motif.trim(), ouvert.sens);
                setEnvoi(false);
                if (ok) setOuvert(null);
              } }} />
        </div>
      )}
      <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '6px 0 0' }}>
        Le passage d’origine n’est jamais modifié : la vérification s’ajoute à côté, avec son auteur et son motif, et la journée est recalculée.
      </p>
    </div>
  );
}

function DialogueCorrection({ j: j0, onFermer, onCorriger, onRouvrir, peutRouvrir, veille }: {
  j: RhJournee; onFermer: () => void; veille?: RhJournee;
  onCorriger: (heures: number, motif: string, reel?: { arrivee?: string; depart?: string }) => Promise<void>;
  onRouvrir: () => Promise<void>;
  peutRouvrir: boolean;
}) {
  const settings = useGed((s) => s.settings);
  /* La journée VIVANTE : vérifier un passage la recalcule, et la fenêtre doit
     montrer le résultat sans être rouverte. */
  const j = useGed((s) => s.rhJournees.find((x) => x.documentId === j0.documentId)) || j0;
  // Proposé : le retenu effectif (le surplus non validé n'y est pas — 30/09/2026).
  const [heures, setHeures] = React.useState(String(j.heuresRetenues ?? j.retenuEffectif ?? j.heuresPointees ?? 0));
  const [motif, setMotif] = React.useState(j.motifEcart || '');
  /* LES HEURES RÉELLES DÉCLARÉES (RQ, 30/09/2026) — l'oubli de badge signalé :
     arrivée à 07:00, badge à 08:30. Le retard se recalcule sur l'heure
     déclarée ; les heures retenues se proposent d'elles-mêmes. */
  const [arrivee, setArrivee] = React.useState(j.arriveeDeclaree ? j.arriveeDeclaree.slice(11, 16) : '');
  const [depart, setDepart] = React.useState(j.departDeclare ? j.departDeclare.slice(11, 16) : '');
  const premiere = (j.segments || []).map((sg) => sg.debut).filter(Boolean).sort()[0] as string | undefined;
  const derniere = (j.segments || []).map((sg) => sg.fin).filter(Boolean).sort().slice(-1)[0] as string | undefined;
  /* LA PROPOSITION SUIT LA RÈGLE DE PAIE (30/09/2026, Naim le 02/09 : 20 h 04
     retenues pour 20 h prévues). Le temps en plus ne se retient pas en
     corrigeant un oubli : on propose le prévu au plus, plus les heures sup
     VALIDÉES. Le surplus se valide à part, avec son motif. */
  const plafonne = (x: number) => {
    const prevu = Number(j.heuresPlanifiees || 0);
    const r = prevu > 0 ? Math.min(x, prevu) + Number(j.heuresSupValidees || 0) : x;
    return Math.round(r * 100) / 100;
  };
  const proposer = (a: string, d: string) => {
    const deb = a ? `${j.date}T${a}:00` : premiere; let fin = d ? `${j.date}T${d}:00` : derniere;
    if (!deb || !fin) return;
    if (fin <= deb) { const x = new Date(`${j.date}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + 1); fin = `${isoJour(x)}T${(d || fin.slice(11, 16))}:00`; }
    const hh = Math.round(((Date.parse(fin) - Date.parse(deb)) / 3600000) * 100) / 100;
    if (hh > 0 && hh <= 24) setHeures(String(plafonne(hh)).replace('.', ','));
  };
  /* LES VACATIONS INCOMPLÈTES (RQ, 30/09/2026 : « comment corriger l'oubli
     de Naim le 02/09 ? ça m'affiche le poste du matin et pas le badgeage du
     soir »). Une journée peut porter deux vacations — le poste du matin ET la
     garde de 19:00. L'arrivée / le départ réels ne visent que le début et la
     fin de la journée ; ici, chaque vacation à laquelle il manque une borne a
     sa propre heure, pré-remplie avec l'heure PRÉVUE. */
  const incompletes = (j.segments || []).map((sg, i) => ({ sg, i }))
    .filter(({ sg }) => (!sg.debut || !sg.fin) && (sg.attenduDebut || sg.attenduFin));
  const [bornes, setBornes] = React.useState<Record<number, string>>({});
  const libVac = (sg: NonNullable<RhJournee['segments']>[number]) =>
    `${sg.poste === 'GARDE' ? 'Garde de nuit' : (sg.poste || 'Vacation')} (${heureJ(sg.attenduDebut, j.date)} → ${heureJ(sg.attenduFin, j.date)})`;
  /* Les heures proposées : chaque vacation, bornes manquantes remplacées par
     l'heure déclarée (la sortie passe au lendemain si besoin). */
  const proposerVacations = (b: Record<number, string>) => {
    let tot = 0;
    for (const [i, sg] of (j.segments || []).entries()) {
      const saisie = b[i];
      const jourDe = (iso?: string | null) => jourIso(iso || '') || j.date;
      const deb = sg.debut || (saisie && !sg.debut ? `${jourDe(sg.attenduDebut)}T${saisie}:00` : null);
      const fin = sg.fin || (saisie && !sg.fin ? `${jourDe(sg.attenduFin)}T${saisie}:00` : null);
      if (deb && fin && fin > deb) tot += (Date.parse(fin) - Date.parse(deb)) / 3600000;
    }
    if (tot > 0 && tot <= 24) setHeures(String(plafonne(tot)).replace('.', ','));
  };
  const declare = !!(arrivee || depart) || Object.values(bornes).some(Boolean);
  /* Les heures d'un enchaînement confirmé : la règle de paie — le prévu au
     plus, plus les heures sup VALIDÉES (le surplus ne part pas en paie). */
  const heuresEnchainement = Math.round((Math.min(Number(j.heuresPointees || 0), Number(j.heuresPlanifiees || 0) || Number(j.heuresPointees || 0))
    + Number(j.heuresSupValidees || 0)) * 100) / 100;
  // Une correction part en paie : deux clics, deux écritures d'écart.
  const [envoi, setEnvoi] = React.useState(false);
  useEscape(!envoi, onFermer);
  const e = ETATS[j.etat] || ETATS.conforme;
  // La virgule est le séparateur décimal du clavier d'ici : « 8,5 » doit valoir
  // 8,5 et non « rien ». Un `<input type="number">` refusait la frappe, laissait
  // la valeur VIDE, et `Number('')` valant 0, l'écran réclamait alors un motif
  // pour un zéro que personne n'avait saisi.
  const saisi = parseFloat(heures.replace(',', '.'));
  const valide = Number.isFinite(saisi) && saisi >= 0 && saisi <= 24;
  const change = valide && Math.abs(saisi - Number(j.heuresPointees || 0)) > 0.01;
  // ⚠️ Ce refus était MUET : le bouton se grisait sans dire pourquoi, et
  // « retenir un horaire différent du pointage demande un motif » — la règle du
  // moteur, à cinq caractères près — n'était écrite nulle part. Un bouton mort
  // sans phrase se lit « le logiciel est cassé », et c'est ce qui a été
  // rapporté le 12/08/2026. On nomme le refus, et on dit ce qui manque.
  const refus = !valide
    ? 'Saisissez les heures retenues (un nombre entre 0 et 24 — la virgule est acceptée).'
    : (change || declare) && motif.trim().length < 5
      ? `Retenir un horaire différent du pointage demande un motif d’au moins 5 caractères${motif.trim() ? ` (${motif.trim().length} pour l’instant)` : ''}.`
      : '';

  return (
    <Modale titre={`${nomDe(j.personnel)} — ${fmtJour(j.date)}`} onFermer={onFermer}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <Chiffre libelle="Pointé (badgeuse)" valeur={h(j.heuresPointees)} />
        <Chiffre libelle="Planifié" valeur={h(j.heuresPlanifiees)} />
      </div>
      <p style={{ fontSize: 12.5, color: e.texte, fontWeight: 600 }}>{e.libelle}</p>
      {j.anomalies?.length > 0 && (
        <div style={{ margin: '4px 0 6px' }}>
          {j.anomalies.map((a) => <PastilleAnomalie key={a} code={a} />)}
        </div>
      )}
      {(j.postes || []).length > 0 && (
        <p style={{ fontSize: 12.5, margin: '4px 0 0', color: 'var(--text-muted)' }}>
          Planifié : {(j.postes || []).map((c) => (c === 'GARDE' ? 'garde de nuit (19:00 → 07:00 (+1))' : c)).join(' · ')}
          {j.apresGarde && <> — <strong>post garde</strong> : horaires aménagés (après-midi à partir de 14 h, matin jusqu’à 11 h)</>}
        </p>
      )}
      {(j.segments || []).length > 0 && (
        /* L10 (11/10/2026) : sous-tableau SIMPLE sur le socle ; le total « Journée » est le PIED, jamais trié avec les vacations. */
        <div style={{ margin: '8px 0 4px' }}>
          <Tableau
            id="rh-vacations-journee"
            niveau="simple"
            densite="compacte"
            lignes={vacationsDe(j).map((v, i) => ({ ...v, cle: `${v.poste}-${i}` }))}
            cleLigne={(v) => v.cle}
            colonnes={[
              { cle: 'poste', libelle: 'Vacation', valeur: (v) => v.poste, rendu: (v) => (v.poste === 'GARDE' ? 'Garde de nuit' : v.poste || '—') },
              { cle: 'prevu', libelle: 'Prévu', valeur: (v) => v.prevu || '', rendu: (v) => <>{v.prevu || '—'}{v.heuresPrevues != null && <div style={{ color: 'var(--text-muted)' }}>{h(v.heuresPrevues)}</div>}</> },
              { cle: 'badge', libelle: 'Badgé', valeur: (v) => v.badge || '', rendu: (v) => <>{v.badge || '—'}{v.manque && <div style={{ color: 'var(--red-600)' }}>{v.manque}</div>}</> },
              { cle: 'heures', libelle: 'Heures badgées', type: 'nombre', valeur: (v) => v.heures ?? null, rendu: (v) => (v.heures != null ? h(v.heures) : '—') },
            ]}
            pied={(c) => (c.cle === 'poste' ? <strong>Journée</strong> : c.cle === 'prevu' ? <strong>{h(j.heuresPlanifiees)}</strong> : c.cle === 'heures' ? <strong>{h(j.heuresPointees)}</strong> : null)}
          />
        </div>
      )}
      <PassagesBadgeuse j={j} veille={veille} />
      {/* CONFIRMER L'ENCHAÎNEMENT EN UN CLIC (RQ, 01/10/2026 : « je ne trouve pas
          comment corriger le problème Autre du 12/09 ») — la présomption « resté
          de la garde au poste, sans badge à la relève » se confirme ici ; les
          heures retenues sont celles de la règle (le prévu au plus). */}
      {/* Le cadre « Confirmer » ne paraît plus que si la journée est BLOQUÉE par
          une autre anomalie : seul, l'enchaînement est prévalidé (01/11/2026). */}
      {!j.validee && !j.corrigePar && j.etat === 'anomalie' && (j.anomalies || []).includes('enchainement_presume') && (
        <div style={{ margin: '10px 0 0', padding: '10px 12px', border: '1px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', background: 'var(--amber-50)' }}>
          <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            <strong>Garde et poste enchaînés sans badge à la relève.</strong> L’outil présume que la personne est restée
            de la garde jusqu’à son poste (relève prise à l’heure du planning). Si c’est bien le cas, confirmez :
            {' '}<strong>{h(heuresEnchainement)}</strong> retenues (le prévu au plus, plus les heures sup validées), motif écrit pour vous.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <Bouton variante="primaire" type="button" disabled={envoi} onClick={async () => {
                setEnvoi(true);
                try {
                  await onCorriger(heuresEnchainement,
                    'Enchaînement garde → poste confirmé : la personne est restée, sans badge à la relève.', {});
                } finally { setEnvoi(false); }
              }}>
              Confirmer l’enchaînement
            </Bouton>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>Sinon, saisissez ci-dessous l’heure réelle ou les heures retenues.</span>
          </div>
        </div>
      )}

      {j.validee ? (
        <>
          <p style={{ fontSize: 12.5, marginTop: 10 }}>
            Journée validée par <strong>{userName(settings, j.valideePar)}</strong>. Elle n’est plus recalculée par un
            import. {j.heuresRetenues != null && <>Retenu : <strong>{h(j.heuresRetenues)}</strong>.</>}
          </p>
          {peutRouvrir && (
            <ConfirmButton label="Rouvrir la journée" variant="outline"
              question="Rouvrir cette journée ? Elle redeviendra modifiable et sera recalculée aux prochains imports."
              confirmer="Rouvrir" onConfirm={onRouvrir} />
          )}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-end' }}>
            <label style={lbl}>Arrivée réelle <span style={{ color: 'var(--text-muted)' }}>(si le badge est tardif ou absent)</span>
              <input type="time" style={{ ...inputStyle, maxWidth: 140 }} value={arrivee}
                onChange={(ev) => { setArrivee(ev.target.value); proposer(ev.target.value, depart); }} />
            </label>
            <label style={lbl}>Départ réel <span style={{ color: 'var(--text-muted)' }}>(si la sortie est oubliée)</span>
              <input type="time" style={{ ...inputStyle, maxWidth: 140 }} value={depart}
                onChange={(ev) => { setDepart(ev.target.value); proposer(arrivee, ev.target.value); }} />
            </label>
          </div>
          {incompletes.length > 0 && (
            <div style={{ marginTop: 12, padding: '8px 10px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', background: 'var(--gray-25)' }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Vacation à compléter</div>
              {incompletes.map(({ sg, i }) => {
                const manque = !sg.debut ? 'entrée' : 'sortie';
                const prevue = String((!sg.debut ? sg.attenduDebut : sg.attenduFin) || '').slice(11, 16);
                return (
                  <label key={i} style={{ ...lbl, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                    <span>{libVac(sg)} — <strong>{manque} non badgée</strong>, heure réelle :</span>
                    <input type="time" style={{ ...inputStyle, maxWidth: 120 }} value={bornes[i] ?? ''} placeholder={prevue}
                      onChange={(ev) => { const b = { ...bornes, [i]: ev.target.value }; setBornes(b); proposerVacations(b); }} />
                    {!bornes[i] && prevue && (
                      <Bouton taille="sm" type="button" onClick={() => {
                          const b = { ...bornes, [i]: prevue }; setBornes(b); proposerVacations(b);
                          if (!motif.trim()) setMotif(`${manque === 'entrée' ? 'Entrée' : 'Sortie'} de ${sg.poste === 'GARDE' ? 'la garde' : 'la vacation'} non badgée, ${manque} à ${prevue} confirmée.`);
                        }}>À l’heure prévue ({prevue})</Bouton>
                    )}
                  </label>
                );
              })}
            </div>
          )}
          {(arrivee || depart) && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
              Le retard sera recalculé sur l’heure déclarée{j.retardMin ? ` (le badge donnait ${j.retardMin} min)` : ''}, et la journée
              comptera comme un <strong>oubli de badgeage régularisé</strong>. Les passages badgés restent visibles.
            </p>
          )}
          <label style={{ ...lbl, marginTop: 12 }}>Heures retenues
            <input type="text" inputMode="decimal" style={{ ...inputStyle, maxWidth: 140 }}
              value={heures} onChange={(ev) => setHeures(ev.target.value)} />
          </label>
          {(change || declare) && (
            <label style={{ ...lbl, marginTop: 10 }}>
              Motif de l’écart <span style={{ color: 'var(--text-muted)' }}>(obligatoire, 5 caractères au minimum)</span>
              <textarea rows={2} style={{ ...inputStyle, width: '100%' }} value={motif} onChange={(ev) => setMotif(ev.target.value)}
                placeholder="Ex. : badge non pointé en sortie, départ à 16h00 confirmé par le responsable." />
            </label>
          )}
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>
            Le pointage d’origine ({h(j.heuresPointees)}) est conservé tel quel. Votre correction
            s’ajoute à côté, à votre nom.
          </p>
          {refus && <FieldError>{refus}</FieldError>}
          <PiedActions taille="sm" style={{ marginTop: 14 }} annuler={{ onClick: onFermer, disabled: envoi }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: envoi, disabled: !!refus || envoi, onClick: async () => {
                setEnvoi(true);
                /* L'heure déclarée d'une vacation est écrite DANS le motif : elle
                   reste lisible au journal, à côté des heures retenues. */
                const vac = incompletes.filter(({ i }) => bornes[i])
                  .map(({ sg, i }) => `${libVac(sg)} : ${!sg.debut ? 'entrée' : 'sortie'} déclarée à ${bornes[i]}`).join(' ; ');
                const m = vac && !motif.includes(vac) ? `${motif.trim()} [${vac}]` : motif.trim();
                /* L'ENTRÉE DE LA PREMIÈRE VACATION = l'ARRIVÉE de la journée, la
                   sortie de la dernière = son DÉPART (01/10/2026, Wafa le 29/09 :
                   l'heure n'était écrite que dans le motif, la journée ne se
                   savait pas à l'heure). Déclarées comme telles, elles
                   recalculent le retard ; une heure du lendemain (fin de
                   garde) reste au motif seul. */
                const segs = j.segments || [];
                const iPrem = segs.map((sg, i) => ({ i, t: sg.attenduDebut || '' })).filter((x) => x.t).sort((a, b) => a.t.localeCompare(b.t))[0]?.i;
                const iDern = segs.map((sg, i) => ({ i, t: sg.attenduFin || '' })).filter((x) => x.t).sort((a, b) => b.t.localeCompare(a.t))[0]?.i;
                let arr = arrivee; let dep = depart;
                if (!arr && iPrem != null && bornes[iPrem] && !segs[iPrem]!.debut) arr = bornes[iPrem]!;
                if (!dep && iDern != null && bornes[iDern] && !segs[iDern]!.fin && jourIso(segs[iDern]!.attenduFin) === j.date) dep = bornes[iDern]!;
                try { await onCorriger(saisi, m, { arrivee: arr, depart: dep }); } finally { setEnvoi(false); }
              } }} />
        </>
      )}
    </Modale>
  );
}

function Chiffre({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div style={{ background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
      <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', fontWeight: 700 }}>{libelle}</div>
      <div style={{ fontSize: 17, fontWeight: 700 }}>{valeur}</div>
    </div>
  );
}

const th: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 'var(--tableau-entete-compacte-police)', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.04em', color: 'var(--text-muted)', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)',
  borderBottom: '1px solid var(--border-default)', background: 'var(--gray-25)', textAlign: 'center',
};
const td: React.CSSProperties = {
  padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderBottom: '1px solid var(--border-subtle)',
  borderInlineStart: '1px solid var(--border-subtle)',
  fontFamily: 'var(--font-sans)', fontSize: 'var(--tableau-ligne-compacte-police)', textAlign: 'center',
};
